import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Package, Video, Truck, RotateCcw, BarChart3, Settings, StopCircle, Download, ClipboardCheck, CheckSquare, Square, FileText } from "lucide-react";
import { Printer } from "lucide-react";
import { Clock3 } from "lucide-react";
import jsPDF from "jspdf";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import * as XLSX from 'xlsx';
import { MecomRecordingCount, MecomRecordingCountCell, MecomRecordingCountHeader, MecomRecordingCountProvider } from "../components/MecomRecordingCount";
import { PublicSignatureQr } from "../components/PublicSignatureQr";

// ═══════════════════════════════════════════════════════════════════
// UTILITIES: Normalização, Feedback Sonoro, Foco Agressivo
// ═══════════════════════════════════════════════════════════════════

function normalizeCodBip(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes("@id@>") || trimmed.includes("@Id@>") || trimmed.includes("@ID@>")) {
    const match = trimmed.match(/@(?:id|Id|ID)@>@?([^@,]+)@?/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  if (trimmed.includes("^id^Ç^") || trimmed.includes("^Id^Ç^") || trimmed.includes("^ID^Ç^")) {
    const match = trimmed.match(/\^(?:id|Id|ID)\^Ç\^([^^,]+)/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  const repeatedMatch = trimmed.match(/^([^,]+),\1(?:,[^,]*)?$/);
  if (repeatedMatch && repeatedMatch[1].trim() !== "") return repeatedMatch[1].trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const id = parsed["id"] ?? parsed["Id"] ?? parsed["ID"];
        if (id !== undefined && id !== null && String(id).trim() !== "") return String(id).trim();
      }
    } catch { /* não é JSON válido */ }
  }
  return trimmed;
}

type DashboardMultiFilters = {
  emitenteIds: number[];
  tipoTransporteIds: number[];
  tipoEnvioIds: number[];
  marketplaceIds: number[];
  pontoOperacionalIds: number[];
};

const emptyDashboardMultiFilters = (): DashboardMultiFilters => ({
  emitenteIds: [],
  tipoTransporteIds: [],
  tipoEnvioIds: [],
  marketplaceIds: [],
  pontoOperacionalIds: [],
});

const copyDashboardMultiFilters = (filters: DashboardMultiFilters): DashboardMultiFilters => ({
  emitenteIds: [...filters.emitenteIds],
  tipoTransporteIds: [...filters.tipoTransporteIds],
  tipoEnvioIds: [...filters.tipoEnvioIds],
  marketplaceIds: [...filters.marketplaceIds],
  pontoOperacionalIds: [...filters.pontoOperacionalIds],
});

function DashboardMultiSelect({ label, options, selectedIds, onToggle, onToggleAll }: {
  label: string;
  options: Array<{ id: number; label: string }>;
  selectedIds: number[];
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[]) => void;
}) {
  const todosSelecionados = options.length > 0 && options.every((option) => selectedIds.includes(option.id));
  const resumo = selectedIds.length === 0 ? "Todos" : `${selectedIds.length} selecionado(s)`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-[200px] justify-between font-normal">
          <span className="truncate">{label}: {resumo}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <label className="flex cursor-pointer items-center gap-2 border-b pb-2 text-sm font-medium">
          <input type="checkbox" checked={todosSelecionados} onChange={() => onToggleAll(options.map((option) => option.id))} />
          Selecionar todos
        </label>
        <div className="mt-2 max-h-60 space-y-2 overflow-y-auto">
          {options.map((option) => (
            <label key={option.id} className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={selectedIds.includes(option.id)} onChange={() => onToggle(option.id)} />
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function playSuccess() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(1100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.08);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
    setTimeout(() => ctx.close(), 200);
  } catch {}
}

function playError() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "square";
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(200, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
    setTimeout(() => ctx.close(), 300);
  } catch {}
}

function forceFocus(ref: React.RefObject<HTMLInputElement | null>) {
  ref.current?.focus();
  requestAnimationFrame(() => ref.current?.focus());
  setTimeout(() => ref.current?.focus(), 50);
  setTimeout(() => ref.current?.focus(), 150);
}

async function uploadWithRetry(formData: FormData, maxRetries = 3): Promise<string> {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      const response = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.url) return result.url;
      throw new Error('Upload retornou sem URL');
    } catch (err) {
      retries++;
      if (retries >= maxRetries) throw err;
      await new Promise(r => setTimeout(r, 1000 * retries));
    }
  }
  throw new Error('Upload falhou após retries');
}

async function validarVideoWebm(videoBlob: Blob): Promise<void> {
  const cabecalho = new Uint8Array(await videoBlob.slice(0, 4).arrayBuffer());
  if (cabecalho.length !== 4 || cabecalho[0] !== 0x1a || cabecalho[1] !== 0x45 || cabecalho[2] !== 0xdf || cabecalho[3] !== 0xa3) {
    throw new Error('A gravação gerada não é um WebM válido.');
  }

  const url = URL.createObjectURL(videoBlob);
  try {
    await new Promise<void>((resolve, reject) => {
      const video = document.createElement('video');
      const limpar = () => {
        video.onloadedmetadata = null;
        video.onerror = null;
        video.removeAttribute('src');
        video.load();
      };
      video.onloadedmetadata = () => {
        limpar();
        resolve();
      };
      video.onerror = () => {
        limpar();
        reject(new Error('A gravação gerada não pôde ser aberta no navegador.'));
      };
      video.preload = 'metadata';
      video.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function confirmarVideoPublicado(url: string, tamanhoEsperado: number): Promise<void> {
  const resposta = await fetch(url, { headers: { Range: 'bytes=0-3' }, cache: 'no-store' });
  if (!resposta.ok) throw new Error(`O vídeo enviado não pôde ser lido: HTTP ${resposta.status}.`);

  const cabecalho = new Uint8Array(await resposta.arrayBuffer());
  if (cabecalho.length !== 4 || cabecalho[0] !== 0x1a || cabecalho[1] !== 0x45 || cabecalho[2] !== 0xdf || cabecalho[3] !== 0xa3) {
    throw new Error('O vídeo enviado não preservou o contêiner WebM.');
  }

  const total = Number(resposta.headers.get('content-range')?.match(/\/(\d+)$/)?.[1] ?? resposta.headers.get('content-length'));
  if (!Number.isFinite(total) || total !== tamanhoEsperado) {
    throw new Error('O tamanho do vídeo enviado não confere com a gravação local.');
  }
}

function baixarVideoFalhado(videoBlob: Blob, nomeArquivo: string) {
  const url = URL.createObjectURL(videoBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function MecomSed() {
  const { user } = useGoogleAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold">MECOM-SED</h1>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard"><BarChart3 className="w-4 h-4 mr-1" />Dashboard</TabsTrigger>
          <TabsTrigger value="tempo-real"><Clock3 className="w-4 h-4 mr-1" />Tempo real</TabsTrigger>
          <TabsTrigger value="entrada"><Package className="w-4 h-4 mr-1" />Bip Entrada</TabsTrigger>
          <TabsTrigger value="gravacao"><Video className="w-4 h-4 mr-1" />Bip Gravação</TabsTrigger>
          <TabsTrigger value="expedicao"><Truck className="w-4 h-4 mr-1" />Bip Expedição</TabsTrigger>
          <TabsTrigger value="devolucao"><RotateCcw className="w-4 h-4 mr-1" />Devolução</TabsTrigger>
          <TabsTrigger value="receber-devolucoes"><ClipboardCheck className="w-4 h-4 mr-1" />Receber Devoluções</TabsTrigger>
          <TabsTrigger value="cadastros"><Settings className="w-4 h-4 mr-1" />Cadastros</TabsTrigger>
          <TabsTrigger value="finalizar-expedir"><ClipboardCheck className="w-4 h-4 mr-1" />Finalizar Expedir</TabsTrigger>
          <TabsTrigger value="romaneios"><FileText className="w-4 h-4 mr-1" />Romaneios</TabsTrigger>
          <TabsTrigger value="reimprimir-etiquetas"><Printer className="w-4 h-4 mr-1" />Reimprimir Etiquetas</TabsTrigger>
          <TabsTrigger value="atrasos-expedicao"><Clock3 className="w-4 h-4 mr-1" />Atrasos Expedição</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab user={user} /></TabsContent>
        <TabsContent value="tempo-real"><TempoRealTab /></TabsContent>
        <TabsContent value="entrada"><BipEntradaTab user={user} /></TabsContent>
        <TabsContent value="gravacao"><BipGravacaoTab user={user} /></TabsContent>
        <TabsContent value="expedicao"><BipExpedicaoTab user={user} /></TabsContent>
        <TabsContent value="devolucao"><DevolucaoTab user={user} /></TabsContent>
        <TabsContent value="receber-devolucoes"><ReceberDevolucoesTab /></TabsContent>
        <TabsContent value="cadastros"><CadastrosTab user={user} /></TabsContent>
        <TabsContent value="finalizar-expedir"><FinalizarExpedirTab user={user} /></TabsContent>
        <TabsContent value="romaneios"><RomaneiosTab /></TabsContent>
        <TabsContent value="reimprimir-etiquetas"><ReimprimirEtiquetasTab /></TabsContent>
        <TabsContent value="atrasos-expedicao"><AtrasosExpedicaoTab user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ReimprimirEtiquetasTab() {
  const [codigoBip, setCodigoBip] = useState("");
  const [codigos, setCodigos] = useState<string[]>([]);
  const [gerando, setGerando] = useState(false);
  const [indisponiveis, setIndisponiveis] = useState<{ codigos: string[]; quantidadeValida: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const adicionarCodigo = () => {
    const codigo = normalizeCodBip(codigoBip);
    setCodigoBip("");
    if (!codigo) return;
    setCodigos((atuais) => atuais.includes(codigo) ? atuais : [...atuais, codigo]);
    forceFocus(inputRef);
  };

  const gerarPdf = async (somenteValidas: boolean) => {
    if (!codigos.length) return;
    const novaAba = window.open("", "_blank");
    setGerando(true);
    try {
      const resposta = await fetch("/api/mecom/reimprimir-etiquetas", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigos, somenteValidas }),
      });
      if (resposta.status === 409) {
        novaAba?.close();
        const resultado = await resposta.json();
        setIndisponiveis({ codigos: resultado.codigosIndisponiveis || [], quantidadeValida: Number(resultado.quantidadeValida || 0) });
        return;
      }
      if (!resposta.ok) {
        novaAba?.close();
        const resultado = await resposta.json().catch(() => ({}));
        throw new Error(resultado.erro || "Não foi possível gerar o PDF.");
      }
      const url = URL.createObjectURL(await resposta.blob());
      if (novaAba) novaAba.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      setIndisponiveis(null);
    } catch (error: any) {
      novaAba?.close();
      toast.error(error.message || "Não foi possível gerar o PDF.");
    } finally {
      setGerando(false);
      forceFocus(inputRef);
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex gap-2">
        <Input ref={inputRef} value={codigoBip} onChange={(e) => setCodigoBip(e.target.value)} onKeyDown={(e) => e.key === "Enter" && adicionarCodigo()} placeholder="Bipe os códigos MECOM..." className="flex-1 font-mono" autoFocus />
        <Button type="button" onClick={adicionarCodigo}>Adicionar</Button>
        <Button type="button" onClick={() => void gerarPdf(false)} disabled={!codigos.length || gerando}><Printer className="w-4 h-4 mr-1" />{gerando ? "Gerando..." : "Gerar PDF"}</Button>
      </div>
      <div className="rounded border">
        <div className="flex items-center justify-between border-b bg-muted/40 p-3"><strong>Códigos bipados ({codigos.length})</strong>{Boolean(codigos.length) && <Button type="button" size="sm" variant="outline" onClick={() => setCodigos([])}>Limpar</Button>}</div>
        <div className="max-h-[420px] overflow-y-auto">{codigos.map((codigo) => <div key={codigo} className="flex items-center justify-between border-b px-3 py-2 font-mono last:border-b-0"><span>{codigo}</span><Button type="button" size="sm" variant="ghost" onClick={() => setCodigos((atuais) => atuais.filter((item) => item !== codigo))}><Trash2 className="h-4 w-4" /></Button></div>)}{!codigos.length && <div className="p-6 text-center text-muted-foreground">Nenhum código bipado.</div>}</div>
      </div>
      <Dialog open={Boolean(indisponiveis)} onOpenChange={(aberto) => { if (!aberto) setIndisponiveis(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Etiquetas não encontradas</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Os códigos abaixo não existem no MECOM ou não possuem etiqueta individual disponível.</p>
          <div className="max-h-64 overflow-y-auto rounded border p-3 font-mono text-sm">{indisponiveis?.codigos.map((codigo) => <div key={codigo}>{codigo}</div>)}</div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setIndisponiveis(null)}>Fechar</Button>{Boolean(indisponiveis?.quantidadeValida) && <Button type="button" onClick={() => void gerarPdf(true)} disabled={gerando}>{gerando ? "Gerando..." : `Imprimir ${indisponiveis?.quantidadeValida} válida(s)`}</Button>}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════════════════════════════════

function DashboardTab({ user }: { user: any }) {
  const isAdmin = user?.role === "admin";
  const [draftFilters, setDraftFilters] = useState<DashboardMultiFilters>(emptyDashboardMultiFilters);
  const [appliedFilters, setAppliedFilters] = useState<DashboardMultiFilters>(emptyDashboardMultiFilters);
  const [statusFilter, setStatusFilter] = useState<string>("__all__");
  const [periodoRapido, setPeriodoRapido] = useState<string>("hoje");
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");

  const getDateRange = () => {
    const hoje = new Date();
    const formatDate = (d: Date) => d.toISOString().split('T')[0];
    if (periodoRapido === 'hoje') return { dataInicio: formatDate(hoje), dataFim: formatDate(new Date(hoje.getTime() + 86400000)) };
    if (periodoRapido === 'ontem') { const ontem = new Date(hoje.getTime() - 86400000); return { dataInicio: formatDate(ontem), dataFim: formatDate(hoje) }; }
    if (periodoRapido === '7dias') { const seteDias = new Date(hoje.getTime() - 7 * 86400000); return { dataInicio: formatDate(seteDias), dataFim: formatDate(new Date(hoje.getTime() + 86400000)) }; }
    if (periodoRapido === 'custom' && dataInicio) return { dataInicio, dataFim: dataFim || formatDate(new Date(hoje.getTime() + 86400000)) };
    return {};
  };
  const dateRange = getDateRange();

  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const { data: tiposTransporte } = trpc.mecom.tiposTransporte.list.useQuery();
  const { data: tiposEnvio } = trpc.mecom.tiposEnvio.list.useQuery();
  const { data: marketplaces } = trpc.mecom.marketplaces.list.useQuery();
  const { data: pontosOperacionais } = trpc.pontosOperacionais.list.useQuery();

  const toggleDraftFilter = (key: keyof DashboardMultiFilters, id: number) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: current[key].includes(id) ? current[key].filter((currentId) => currentId !== id) : [...current[key], id],
    }));
  };

  const toggleAllDraftFilter = (key: keyof DashboardMultiFilters, ids: number[]) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: ids.length > 0 && ids.every((id) => current[key].includes(id)) ? [] : ids,
    }));
  };

  const applyDashboardFilters = () => setAppliedFilters(copyDashboardMultiFilters(draftFilters));
  const clearDashboardFilters = () => {
    const emptyFilters = emptyDashboardMultiFilters();
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const { data: dashboard } = trpc.mecom.dashboard.useQuery({
    tipoTransporteIds: appliedFilters.tipoTransporteIds.length ? appliedFilters.tipoTransporteIds : undefined,
    tipoEnvioIds: appliedFilters.tipoEnvioIds.length ? appliedFilters.tipoEnvioIds : undefined,
    marketplaceIds: appliedFilters.marketplaceIds.length ? appliedFilters.marketplaceIds : undefined,
    pontoOperacionalIds: appliedFilters.pontoOperacionalIds.length ? appliedFilters.pontoOperacionalIds : undefined,
    emitenteIds: appliedFilters.emitenteIds.length ? appliedFilters.emitenteIds : undefined,
    statusFilter: statusFilter !== "__all__" ? statusFilter : undefined,
    dataInicio: dateRange.dataInicio || undefined,
    dataFim: dateRange.dataFim || undefined,
  });

  const utils = trpc.useUtils();
  const deletePedidoMut = trpc.mecom.deletePedido.useMutation({ onSuccess: () => { utils.mecom.dashboard.invalidate(); toast.success("Bip excluído!"); } });

  const counts = dashboard?.counts || [];
  const recentBips = dashboard?.recentBips || [];
  const [searchTerm, setSearchTerm] = useState("");
  const filteredBips = useMemo(() => {
    if (!searchTerm) return recentBips;
    const term = normalizeCodBip(searchTerm).toLowerCase();
    return recentBips.filter((b: any) => {
      const fields = [
        b.codigoBip,
        b.lojaNome,
        b.envioNome,
        b.transportadoraNome,
        b.status,
        b.pontoOperacionalTipo ? `${b.pontoOperacionalTipo} - #${b.pontoOperacionalId}` : '',
      ].map(f => (f || '').toLowerCase());
      return fields.some(f => f.includes(term));
    });
  }, [recentBips, searchTerm]);
  const getCount = (status: string) => counts.find((c: any) => c.status === status)?.total || 0;

  const handleDeleteBip = (id: number, codigo: string) => {
    if (confirm(`Tem certeza que deseja excluir o bip ${codigo}?`)) {
      deletePedidoMut.mutate({ id });
    }
  };

  const handleExportExcel = () => {
    const data = recentBips.map((b: any) => ({
      'Código': b.codigoBip,
      'Loja': b.lojaNome || '-',
      'Envio': b.envioNome || '-',
      'Transportadora': b.transportadoraNome || '-',
      'Status': b.status,
      'Entrada': b.bipEntradaEm ? new Date(b.bipEntradaEm).toLocaleString('pt-BR') : '-',
      'Gravação': b.bipGravacaoEm ? new Date(b.bipGravacaoEm).toLocaleString('pt-BR') : '-',
      'Ponto Operacional': b.pontoOperacionalTipo ? `${b.pontoOperacionalTipo} - #${b.pontoOperacionalId}` : '-',
      'Expedição': b.bipExpedicaoEm ? new Date(b.bipExpedicaoEm).toLocaleString('pt-BR') : '-',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bips');
    XLSX.writeFile(wb, 'mecom_bips.xlsx');
  };

  const bancadaCounts = recentBips.reduce((acc: any, b: any) => {
    if (b.pontoOperacionalId) {
      const key = `${b.pontoOperacionalTipo || 'Bancada'} - #${b.pontoOperacionalId}`;
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => setPeriodoRapido('hoje')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'hoje' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Hoje</button>
        <button onClick={() => setPeriodoRapido('ontem')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'ontem' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Ontem</button>
        <button onClick={() => setPeriodoRapido('7dias')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === '7dias' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Últimos 7 dias</button>
        <button onClick={() => setPeriodoRapido('todos')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'todos' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Todos</button>
        <button onClick={() => setPeriodoRapido('custom')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'custom' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Período</button>
        {periodoRapido === 'custom' && (
          <>
            <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            <span className="text-sm">até</span>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => setStatusFilter('__all__')} className={`px-3 py-1.5 rounded text-sm font-medium ${statusFilter === '__all__' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Todos</button>
        <button onClick={() => setStatusFilter('sem_gravacao')} className={`px-3 py-1.5 rounded text-sm font-medium ${statusFilter === 'sem_gravacao' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Sem Gravação</button>
        <button onClick={() => setStatusFilter('sem_expedicao')} className={`px-3 py-1.5 rounded text-sm font-medium ${statusFilter === 'sem_expedicao' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Sem Expedição</button>
        <button onClick={() => setStatusFilter('devolucao_pendente')} className={`px-3 py-1.5 rounded text-sm font-medium ${statusFilter === 'devolucao_pendente' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Devolução Pendente</button>
      </div>
      <div className="flex flex-wrap gap-3">
        {isAdmin && (
          <DashboardMultiSelect
            label="Emitente"
            options={(emitentes as any[] || []).map((emitente: any) => ({ id: emitente.id, label: emitente.razaoSocial }))}
            selectedIds={draftFilters.emitenteIds}
            onToggle={(id) => toggleDraftFilter("emitenteIds", id)}
            onToggleAll={(ids) => toggleAllDraftFilter("emitenteIds", ids)}
          />
        )}
        <DashboardMultiSelect
          label="Transporte"
          options={(tiposTransporte as any[] || []).map((tipo: any) => ({ id: tipo.id, label: tipo.nome }))}
          selectedIds={draftFilters.tipoTransporteIds}
          onToggle={(id) => toggleDraftFilter("tipoTransporteIds", id)}
          onToggleAll={(ids) => toggleAllDraftFilter("tipoTransporteIds", ids)}
        />
        <DashboardMultiSelect
          label="Envio"
          options={(tiposEnvio as any[] || []).map((tipo: any) => ({ id: tipo.id, label: tipo.nome }))}
          selectedIds={draftFilters.tipoEnvioIds}
          onToggle={(id) => toggleDraftFilter("tipoEnvioIds", id)}
          onToggleAll={(ids) => toggleAllDraftFilter("tipoEnvioIds", ids)}
        />
        <DashboardMultiSelect
          label="Marketplace"
          options={(marketplaces as any[] || []).map((marketplace: any) => ({ id: marketplace.id, label: marketplace.nome }))}
          selectedIds={draftFilters.marketplaceIds}
          onToggle={(id) => toggleDraftFilter("marketplaceIds", id)}
          onToggleAll={(ids) => toggleAllDraftFilter("marketplaceIds", ids)}
        />
        <DashboardMultiSelect
          label="Ponto Operacional"
          options={(pontosOperacionais as any[] || []).map((ponto: any) => ({ id: ponto.id, label: `${ponto.tipo} - #${ponto.id}` }))}
          selectedIds={draftFilters.pontoOperacionalIds}
          onToggle={(id) => toggleDraftFilter("pontoOperacionalIds", id)}
          onToggleAll={(ids) => toggleAllDraftFilter("pontoOperacionalIds", ids)}
        />
        <Button onClick={applyDashboardFilters}>Aplicar filtros</Button>
        <Button variant="outline" onClick={clearDashboardFilters}>Limpar filtros</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Entrada</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{getCount('entrada')}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Gravado</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{getCount('gravado')}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Expedido</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{getCount('expedido')}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Devolvido</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{getCount('devolvido')}</p></CardContent></Card>
      </div>

      {Object.keys(bancadaCounts).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Bancadas</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-x-6 gap-y-0">
              {(() => {
                const entries = Object.entries(bancadaCounts).sort((a, b) => (b[1] as number) - (a[1] as number));
                const columns: [string, unknown][][] = [];
                for (let i = 0; i < entries.length; i += 4) {
                  columns.push(entries.slice(i, i + 4));
                }
                return columns.map((col, ci) => (
                  <div key={ci} className="flex flex-col">
                    {col.map(([bancada, qty]) => (
                      <span key={bancada} className="text-xs">{bancada}: <strong>{qty as number}</strong></span>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {recentBips.length > 0 && (
        <div className="border rounded-lg p-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-semibold">Log de Bips Recentes</h4>
            <div className="flex gap-2 items-center">
              <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-64 h-8 text-sm" />
              <Button size="sm" variant="outline" onClick={handleExportExcel}><Download className="w-4 h-4 mr-1" />Exportar Excel</Button>
            </div>
          </div>
          <div className="max-h-[400px] overflow-auto">
            <MecomRecordingCountProvider codigoBips={filteredBips.map((b: any) => b.codigoBip)}>
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left">Código</th>
                    <th className="p-2 text-left">Loja</th>
                    <th className="p-2 text-left">Envio</th>
                    <th className="p-2 text-left">Transportadora</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Entrada</th>
                    <th className="p-2 text-left">Gravação</th>
                    <MecomRecordingCountHeader />
                    <th className="p-2 text-left">Ponto Operacional</th>
                    <th className="p-2 text-left">Expedição</th>
                    <th className="p-2 text-left">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBips.map((b: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 font-mono">{b.codigoBip}</td>
                      <td className="p-2">{b.lojaNome || '-'}</td>
                      <td className="p-2">{b.envioNome || '-'}</td>
                      <td className="p-2">{b.transportadoraNome || '-'}</td>
                      <td className="p-2">{b.status}</td>
                      <td className="p-2">{b.bipEntradaEm ? new Date(b.bipEntradaEm).toLocaleString('pt-BR') : '-'}</td>
                      <td className="p-2">{b.bipGravacaoEm ? new Date(b.bipGravacaoEm).toLocaleString('pt-BR') : '-'}</td>
                      <MecomRecordingCountCell codigoBip={b.codigoBip} />
                      <td className="p-2">{b.pontoOperacionalTipo ? `${b.pontoOperacionalTipo} - #${b.pontoOperacionalId}` : '-'}</td>
                      <td className="p-2">{b.bipExpedicaoEm ? new Date(b.bipExpedicaoEm).toLocaleString('pt-BR') : '-'}</td>
                      <td className="p-2"><Button size="sm" variant="ghost" onClick={() => handleDeleteBip(b.id, b.codigoBip)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </MecomRecordingCountProvider>
          </div>
        </div>
      )}
    </div>
  );
}

function TempoRealTab() {
  const [agora, setAgora] = useState(() => new Date());
  const [linhaSelecionada, setLinhaSelecionada] = useState<any | null>(null);
  const utils = trpc.useUtils();
  const { data: linhas, isLoading, refetch } = trpc.mecom.tempoReal.list.useQuery(undefined, { refetchInterval: 60000 });
  const { data: bancadas, refetch: refetchBancadas } = trpc.mecom.tempoReal.bancadas.useQuery(undefined, { refetchInterval: 60000 });

  useEffect(() => {
    const timer = window.setInterval(() => setAgora(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const horarioBrasilia = useMemo(() => {
    const partes = new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(agora);
    const valor = (tipo: string) => Number(partes.find((parte) => parte.type === tipo)?.value || 0);
    const horas = valor("hour");
    const minutos = valor("minute");
    const segundos = valor("second");
    return { texto: `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}`, totalSegundos: horas * 3600 + minutos * 60 + segundos };
  }, [agora]);

  const itens = useMemo(() => {
    const calcularRestante = (horaCorte?: string | null) => {
      if (!horaCorte) return null;
      const [horas, minutos] = horaCorte.split(":").map(Number);
      return horas * 3600 + minutos * 60 - horarioBrasilia.totalSegundos;
    };
    return (linhas as any[] || []).map((linha) => {
      const entradasHoje = Number(linha.entradasHoje || 0);
      const gravadosHoje = Number(linha.gravadosHoje || 0);
      const expedidosHoje = Number(linha.expedidosHoje || 0);
      const restanteSegundos = calcularRestante(linha.horaCorte);
      const status = entradasHoje === gravadosHoje
        ? "verde"
        : restanteSegundos !== null && restanteSegundos < 30 * 60
          ? "vermelho"
          : restanteSegundos !== null && restanteSegundos < 60 * 60
            ? "amarelo"
            : "sem-cor";
      const ordemStatus = { vermelho: 0, amarelo: 1, "sem-cor": 2, verde: 3 }[status];
      return { ...linha, entradasHoje, gravadosHoje, expedidosHoje, restanteSegundos, status, ordemStatus };
    }).sort((a, b) => a.ordemStatus - b.ordemStatus || (a.restanteSegundos ?? Number.POSITIVE_INFINITY) - (b.restanteSegundos ?? Number.POSITIVE_INFINITY));
  }, [linhas, horarioBrasilia]);

  const formatarRestante = (segundos: number | null) => {
    if (segundos === null) return "—";
    const absoluto = Math.abs(segundos);
    const horas = Math.floor(absoluto / 3600);
    const minutos = Math.floor((absoluto % 3600) / 60);
    return `${segundos < 0 ? "Atrasado " : ""}${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}`;
  };

  const formatarDuracaoMedia = (segundos: number | null) => {
    if (segundos === null || !Number.isFinite(segundos)) return "—";
    const valorArredondado = Math.round(segundos);
    const minutos = Math.floor(valorArredondado / 60);
    const segundosRestantes = valorArredondado % 60;
    return `${String(minutos).padStart(2, "0")}:${String(segundosRestantes).padStart(2, "0")}`;
  };

  const formatarHoras = (segundos: number | null) => {
    if (segundos === null || !Number.isFinite(segundos)) return "00:00:00";
    const valorArredondado = Math.round(segundos);
    const horas = Math.floor(valorArredondado / 3600);
    const minutos = Math.floor((valorArredondado % 3600) / 60);
    const segundosRestantes = valorArredondado % 60;
    return `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}:${String(segundosRestantes).padStart(2, "0")}`;
  };

  const classeLinha = (status: string) => ({
    vermelho: "bg-red-200",
    amarelo: "bg-yellow-200",
    verde: "bg-green-200",
    "sem-cor": "",
  }[status] || "");

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="text-lg font-semibold">Tempo real</h2><p className="text-sm text-muted-foreground">Hoje, agrupado por tipo de envio e transportadora.</p></div>
        <div className="flex items-center gap-3"><span className="text-sm text-muted-foreground">Atualizado às {horarioBrasilia.texto}</span><Button type="button" variant="outline" onClick={() => { refetch(); refetchBancadas(); }} disabled={isLoading}>Atualizar</Button></div>
      </div>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-muted"><tr><th className="p-3 text-left">Envio • Transportadora</th><th className="p-3 text-left">Hora de corte</th><th className="p-3 text-left">Resta</th><th className="p-3 text-right">Entrada hoje</th><th className="p-3 text-right">Gravado hoje</th><th className="p-3 text-right">Expedido hoje</th></tr></thead>
              <tbody>{itens.map((linha: any) => <tr key={`${linha.tipoEnvioId}-${linha.tipoTransporteId}`} className={`border-t ${classeLinha(linha.status)}`}><td className="p-3 font-medium"><Button type="button" variant="link" className="h-auto p-0 font-medium" onClick={() => setLinhaSelecionada(linha)}>{linha.envioNome} • {linha.transporteNome}</Button></td><td className="p-3">{linha.horaCorte || "Não cadastrado"}</td><td className="p-3 font-medium">{formatarRestante(linha.restanteSegundos)}</td><td className="p-3 text-right">{linha.entradasHoje}</td><td className="p-3 text-right">{linha.gravadosHoje}</td><td className="p-3 text-right">{linha.expedidosHoje}</td></tr>)}</tbody>
            </table>
            {!isLoading && !itens.length && <p className="p-6 text-center text-sm text-muted-foreground">Nenhuma combinação com entrada e expedição diferentes hoje.</p>}
            {isLoading && <p className="p-6 text-center text-sm text-muted-foreground">Carregando...</p>}
          </div>
        </CardContent>
      </Card>
      <p className="text-sm text-muted-foreground">Ordem: vermelho, amarelo, sem cor e verde; dentro de cada grupo, menor tempo restante primeiro. A linha sai quando Entrada hoje = Expedido hoje.</p>
      {(bancadas as any[] || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Gravações por bancada — hoje</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-auto">
              <table className="w-full min-w-[600px] text-sm">
                <thead className="bg-muted"><tr><th className="p-3 text-left">Bancada</th><th className="p-3 text-right">Gravadas hoje</th><th className="p-3 text-right">Média de gravação</th><th className="p-3 text-right">Horas</th></tr></thead>
                <tbody>{(bancadas as any[]).map((bancada: any) => <tr key={bancada.id} className="border-t"><td className="p-3 font-medium">{bancada.nome ? `${bancada.nome} — ${bancada.tipo} #${bancada.id}` : `${bancada.tipo} - #${bancada.id}`}</td><td className="p-3 text-right">{Number(bancada.gravacoesHoje || 0)}</td><td className="p-3 text-right">{formatarDuracaoMedia(bancada.duracaoMediaSegundos === null ? null : Number(bancada.duracaoMediaSegundos))}</td><td className="p-3 text-right">{formatarHoras(bancada.duracaoTotalSegundos === null ? null : Number(bancada.duracaoTotalSegundos))}</td></tr>)}</tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {linhaSelecionada && <TempoRealDetalheModal linha={linhaSelecionada} aoFechar={() => setLinhaSelecionada(null)} />}
    </div>
  );
}

async function baixarPdfListaSeparacaoTempoReal(dados: any) {
  const { default: autoTable } = await import("jspdf-autotable");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("LISTA DE SEPARAÇÃO", 105, 15, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Origem: ${dados.origem}`, 14, 23);
  const vinculosMeccom = Array.isArray(dados.vinculosMeccom) && dados.vinculosMeccom.length
    ? dados.vinculosMeccom.join(" • ")
    : "-";
  const linhasVinculosMeccom = doc.splitTextToSize(`Vínculos MECOM: ${vinculosMeccom}`, 182);
  doc.text(linhasVinculosMeccom, 14, 28);
  const yGeradaEm = 28 + (linhasVinculosMeccom.length * 4) + 1;
  doc.text(`Gerada em: ${new Date(dados.geradoEm).toLocaleString("pt-BR")}`, 14, yGeradaEm);
  doc.text(`Gerada por: ${dados.geradoPorNome}`, 14, yGeradaEm + 5);
  autoTable(doc, {
    startY: yGeradaEm + 10,
    head: [["SKU", "Descrição", "Quantidade", "Localizações", "Outros Endereços"]],
    body: dados.itens.map((item: any) => [
      item.sku,
      item.descricao || "-",
      Number(item.quantidade).toLocaleString("pt-BR", { maximumFractionDigits: 4 }),
      item.localizacoes.length ? item.localizacoes.join(", ") : "-",
      item.outrosEnderecos.length ? item.outrosEnderecos.join(", ") : "-",
    ]),
    styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
    headStyles: { fillColor: [31, 41, 55] },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 54 },
      2: { cellWidth: 22, halign: "right" },
      3: { cellWidth: 39 },
      4: { cellWidth: 39 },
    },
  });
  doc.save("lista_separacao_tempo_real.pdf");
}

function TempoRealDetalheModal({ linha, aoFechar }: { linha: any; aoFechar: () => void }) {
  const [abaAtiva, setAbaAtiva] = useState("a-gravar");
  const [dataReferencia, setDataReferencia] = useState("");
  const [gerando, setGerando] = useState(false);
  const [gerandoLista, setGerandoLista] = useState(false);
  const [indisponiveis, setIndisponiveis] = useState<{ codigos: string[]; quantidadeValida: number; solicitados: string[] } | null>(null);
  const [codigoBipsOmitidos, setCodigoBipsOmitidos] = useState<string[] | null>(null);
  const { data: detalhe, isLoading } = trpc.mecom.tempoReal.detalhe.useQuery({ tipoEnvioId: Number(linha.tipoEnvioId), tipoTransporteId: Number(linha.tipoTransporteId), dataReferencia: dataReferencia || undefined }, { refetchInterval: 60000 });
  const gerarListaSeparacao = trpc.mecom.tempoReal.gerarListaSeparacao.useMutation();
  const aGravar = detalhe?.aGravar || [];
  const aExpedir = detalhe?.aExpedir || [];

  const formatarHora = (valor: any) => valor ? new Date(valor).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—";

  const gerarPdf = async (codigos: string[], somenteValidas: boolean) => {
    if (!codigos.length) return;
    const novaAba = window.open("", "_blank");
    setGerando(true);
    try {
      const resposta = await fetch("/api/mecom/reimprimir-etiquetas", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigos, somenteValidas }),
      });
      if (resposta.status === 409) {
        novaAba?.close();
        const resultado = await resposta.json();
        setIndisponiveis({ codigos: resultado.codigosIndisponiveis || [], quantidadeValida: Number(resultado.quantidadeValida || 0), solicitados: codigos });
        return;
      }
      if (!resposta.ok) {
        novaAba?.close();
        const resultado = await resposta.json().catch(() => ({}));
        throw new Error(resultado.erro || "Não foi possível gerar o PDF.");
      }
      const url = URL.createObjectURL(await resposta.blob());
      if (novaAba) novaAba.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      setIndisponiveis(null);
    } catch (error: any) {
      novaAba?.close();
      toast.error(error.message || "Não foi possível gerar o PDF.");
    } finally {
      setGerando(false);
    }
  };

  const gerarListaPorAba = async (pedidos: any[], pendencia: "aGravar" | "aExpedir") => {
    if (!pedidos.length) return;
    setGerandoLista(true);
    try {
      const dados = await gerarListaSeparacao.mutateAsync({
        codigoBips: pedidos.map((pedido: any) => String(pedido.codigoBip)),
        tipoEnvioId: Number(linha.tipoEnvioId),
        tipoTransporteId: Number(linha.tipoTransporteId),
        pendencia,
        dataReferencia: dataReferencia || undefined,
      });
      if (!dados.itens.length) {
        if (dados.codigoBipsOmitidos.length) setCodigoBipsOmitidos(dados.codigoBipsOmitidos);
        toast.error("Nenhum pedido desta aba possui SKU de etiqueta para gerar a lista.");
        return;
      }
      await baixarPdfListaSeparacaoTempoReal(dados);
      toast.success("Lista de separação gerada.");
      if (dados.codigoBipsOmitidos.length) setCodigoBipsOmitidos(dados.codigoBipsOmitidos);
    } catch (error: any) {
      toast.error(error.message || "Não foi possível gerar a lista de separação.");
    } finally {
      setGerandoLista(false);
    }
  };

  const tabelaPedidos = (pedidos: any[], mensagemVazia: string, pendencia: "aGravar" | "aExpedir") => (
    <div className="space-y-3 pt-3">
      <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => void gerarListaPorAba(pedidos, pendencia)} disabled={!pedidos.length || gerandoLista}><ClipboardCheck className="w-4 h-4 mr-1" />{gerandoLista ? "Gerando lista..." : "Gerar lista de separação"}</Button><Button type="button" onClick={() => void gerarPdf(pedidos.map((pedido: any) => String(pedido.codigoBip)), false)} disabled={!pedidos.length || gerando}><Download className="w-4 h-4 mr-1" />{gerando ? "Gerando..." : "Baixar todas as etiquetas"}</Button></div>
      <div className="overflow-auto rounded border">
        <table className="w-full min-w-[780px] text-sm">
          <thead className="bg-muted"><tr><th className="p-3 text-left">Pedido</th><th className="p-3 text-left">BIP</th><th className="p-3 text-left">Hora de entrada</th><th className="p-3 text-left">Hora de gravação</th><th className="p-3 text-left">Loja</th><th className="p-3 text-right">Etiqueta</th></tr></thead>
          <tbody>{pedidos.map((pedido: any) => <tr key={pedido.id} className="border-t"><td className="p-3">#{pedido.id}</td><td className="p-3 font-mono">{pedido.codigoBip}</td><td className="p-3 whitespace-nowrap">{formatarHora(pedido.bipEntradaEm)}</td><td className="p-3 whitespace-nowrap">{formatarHora(pedido.bipGravacaoEm)}</td><td className="p-3">{pedido.lojaNome || "-"}</td><td className="p-3 text-right"><Button type="button" size="sm" variant="outline" onClick={() => void gerarPdf([String(pedido.codigoBip)], false)} disabled={gerando}><Printer className="w-4 h-4 mr-1" />Etiqueta</Button></td></tr>)}</tbody>
        </table>
        {!isLoading && !pedidos.length && <p className="p-6 text-center text-sm text-muted-foreground">{mensagemVazia}</p>}
        {isLoading && <p className="p-6 text-center text-sm text-muted-foreground">Carregando...</p>}
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={true} onOpenChange={(aberto) => { if (!aberto) aoFechar(); }}>
      <DialogContent className="!w-[90vw] !h-[85vh] !max-w-none overflow-y-auto">
        <DialogHeader><DialogTitle>{linha.envioNome} • {linha.transporteNome}</DialogTitle></DialogHeader>
        <div className="space-y-1"><label htmlFor="tempo-real-periodo" className="text-sm">Período</label><Input id="tempo-real-periodo" type="date" className="w-44" value={dataReferencia || new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" })} onChange={(evento) => setDataReferencia(evento.target.value)} /></div>
        <Tabs value={abaAtiva} onValueChange={setAbaAtiva}>
          <TabsList><TabsTrigger value="a-gravar">Falta (A gravar) ({aGravar.length})</TabsTrigger><TabsTrigger value="a-expedir">Falta (A expedir) ({aExpedir.length})</TabsTrigger></TabsList>
          <TabsContent value="a-gravar">{tabelaPedidos(aGravar, dataReferencia ? "Nenhum pedido na data selecionada aguardando gravação." : "Nenhum pedido de hoje aguardando gravação.", "aGravar")}</TabsContent>
          <TabsContent value="a-expedir">{tabelaPedidos(aExpedir, dataReferencia ? "Nenhum pedido na data selecionada aguardando expedição." : "Nenhum pedido de hoje aguardando expedição.", "aExpedir")}</TabsContent>
        </Tabs>
      </DialogContent>
      </Dialog>
      <Dialog open={Boolean(indisponiveis)} onOpenChange={(aberto) => { if (!aberto) setIndisponiveis(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Etiquetas não encontradas</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Os códigos abaixo não existem no MECOM ou não possuem etiqueta individual disponível.</p>
          <div className="max-h-64 overflow-y-auto rounded border p-3 font-mono text-sm">{indisponiveis?.codigos.map((codigo) => <div key={codigo}>{codigo}</div>)}</div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setIndisponiveis(null)}>Fechar</Button>{Boolean(indisponiveis?.quantidadeValida) && <Button type="button" onClick={() => void gerarPdf(indisponiveis?.solicitados || [], true)} disabled={gerando}>{gerando ? "Gerando..." : `Imprimir ${indisponiveis?.quantidadeValida} válida(s)`}</Button>}</div>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(codigoBipsOmitidos)} onOpenChange={(aberto) => { if (!aberto) setCodigoBipsOmitidos(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pedidos sem SKU de etiqueta</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A lista foi gerada com os demais pedidos. Os BIPs abaixo foram omitidos porque não possuem SKU de etiqueta disponível.</p>
          <div className="max-h-64 overflow-y-auto rounded border p-3 font-mono text-sm">{codigoBipsOmitidos?.map((codigo) => <div key={codigo}>{codigo}</div>)}</div>
          <div className="flex justify-end"><Button type="button" onClick={() => setCodigoBipsOmitidos(null)}>Fechar</Button></div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AtrasosExpedicaoTab({ user }: { user: any }) {
  const isAdmin = user?.role === "admin";
  const [periodo, setPeriodo] = useState("hoje");
  const [dataInicioManual, setDataInicioManual] = useState("");
  const [dataFimManual, setDataFimManual] = useState("");
  const [emitenteId, setEmitenteId] = useState<string>("__all__");
  const [marketplaceId, setMarketplaceId] = useState<string>("__all__");
  const [tipoEnvioId, setTipoEnvioId] = useState<string>("__all__");
  const [tipoTransporteId, setTipoTransporteId] = useState<string>("__all__");
  const [motivoFiltro, setMotivoFiltro] = useState<string>("sem_motivo");
  const [busca, setBusca] = useState("");
  const [edicoes, setEdicoes] = useState<Record<number, { motivo: string; motivoOutro: string }>>({});
  const utils = trpc.useUtils();

  const faixaData = useMemo(() => {
    const formatarData = (data: Date) => data.toISOString().slice(0, 10);
    const hoje = new Date();
    const amanha = new Date(hoje.getTime() + 86400000);
    if (periodo === "hoje") return { dataInicio: formatarData(hoje), dataFim: formatarData(amanha) };
    if (periodo === "ontem") return { dataInicio: formatarData(new Date(hoje.getTime() - 86400000)), dataFim: formatarData(hoje) };
    if (periodo === "ultimos30dias") return { dataInicio: formatarData(new Date(hoje.getTime() - 29 * 86400000)), dataFim: formatarData(amanha) };
    if (periodo === "manual" && dataInicioManual) {
      const fim = dataFimManual ? new Date(`${dataFimManual}T00:00:00`) : hoje;
      return { dataInicio: dataInicioManual, dataFim: formatarData(new Date(fim.getTime() + 86400000)) };
    }
    return {};
  }, [periodo, dataInicioManual, dataFimManual]);

  const filtrosConsulta = useMemo(() => ({
    emitenteId: isAdmin && emitenteId !== "__all__" ? Number(emitenteId) : undefined,
    marketplaceId: marketplaceId !== "__all__" ? Number(marketplaceId) : undefined,
    tipoEnvioId: tipoEnvioId !== "__all__" ? Number(tipoEnvioId) : undefined,
    tipoTransporteId: tipoTransporteId !== "__all__" ? Number(tipoTransporteId) : undefined,
    motivo: motivoFiltro !== "__all__" ? motivoFiltro as "sem_motivo" | "outro" | "buscas_produto" | "pedido_dia_seguinte" | "falha_operacional_esquecido" : undefined,
    search: busca.trim() || undefined,
    dataInicio: faixaData.dataInicio,
    dataFim: faixaData.dataFim,
  }), [isAdmin, emitenteId, marketplaceId, tipoEnvioId, tipoTransporteId, motivoFiltro, busca, faixaData]);

  const filtrosResumo = useMemo(() => ({
    emitenteId: isAdmin && emitenteId !== "__all__" ? Number(emitenteId) : undefined,
    marketplaceId: marketplaceId !== "__all__" ? Number(marketplaceId) : undefined,
    tipoEnvioId: tipoEnvioId !== "__all__" ? Number(tipoEnvioId) : undefined,
    tipoTransporteId: tipoTransporteId !== "__all__" ? Number(tipoTransporteId) : undefined,
  }), [isAdmin, emitenteId, marketplaceId, tipoEnvioId, tipoTransporteId]);

  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const { data: marketplaces } = trpc.mecom.marketplaces.list.useQuery();
  const { data: tiposEnvio } = trpc.mecom.tiposEnvio.list.useQuery();
  const { data: tiposTransporte } = trpc.mecom.tiposTransporte.list.useQuery();
  const { data: atrasos, isLoading } = trpc.mecom.atrasosExpedicao.list.useQuery(filtrosConsulta);
  const { data: resumo } = trpc.mecom.atrasosExpedicao.resumo.useQuery(filtrosResumo);
  const salvarAtraso = trpc.mecom.atrasosExpedicao.salvar.useMutation({
    onSuccess: async () => {
      setMotivoFiltro("sem_motivo");
      await Promise.all([
        utils.mecom.atrasosExpedicao.list.invalidate(),
        utils.mecom.atrasosExpedicao.resumo.invalidate(),
      ]);
      toast.success("Motivo do atraso salvo.");
    },
    onError: (erro: any) => toast.error(erro.message || "Não foi possível salvar o motivo."),
  });

  const opcoesMotivo = [
    { value: "outro", label: "Outro" },
    { value: "buscas_produto", label: "Fazendo buscas pelo produto" },
    { value: "pedido_dia_seguinte", label: "Pedido para o dia seguinte" },
    { value: "falha_operacional_esquecido", label: "Falha operacional, pedido esquecido" },
  ];

  const rotuloMotivo = (motivo?: string | null) => opcoesMotivo.find((opcao) => opcao.value === motivo)?.label || "Sem motivo";
  const valorEdicao = (pedido: any) => edicoes[pedido.id] || { motivo: pedido.motivoAtraso || "__none__", motivoOutro: pedido.motivoAtrasoOutro || "" };
  const atualizarEdicao = (pedido: any, dados: Partial<{ motivo: string; motivoOutro: string }>) => {
    const atual = valorEdicao(pedido);
    setEdicoes((edicoesAtuais) => ({ ...edicoesAtuais, [pedido.id]: { ...atual, ...dados } }));
  };
  const salvarPedido = (pedido: any) => {
    const edicao = valorEdicao(pedido);
    if (edicao.motivo === "__none__") {
      toast.error("Selecione o motivo do atraso.");
      return;
    }
    if (edicao.motivo === "outro" && !edicao.motivoOutro.trim()) {
      toast.error("Preencha o motivo para a opção Outro.");
      return;
    }
    salvarAtraso.mutate({
      pedidoId: pedido.id,
      motivo: edicao.motivo as "outro" | "buscas_produto" | "pedido_dia_seguinte" | "falha_operacional_esquecido",
      motivoOutro: edicao.motivo === "outro" ? edicao.motivoOutro.trim() : undefined,
    });
  };

  const renderResumo = (titulo: string, linhas: any[]) => (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{titulo}</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-64 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted"><tr><th className="p-2 text-left">Emitente</th><th className="p-2 text-left">Tipo de entrega</th><th className="p-2 text-left">Transportadora</th><th className="p-2 text-right">Atrasados / entradas</th><th className="p-2 text-right">%</th></tr></thead>
            <tbody>{linhas.map((linha: any, indice: number) => <tr key={`${linha.emitenteNome}-${linha.tipoEnvioNome}-${linha.transporteNome}-${indice}`} className="border-t"><td className="p-2">{linha.emitenteNome}</td><td className="p-2">{linha.tipoEnvioNome}</td><td className="p-2">{linha.transporteNome}</td><td className="p-2 text-right">{linha.totalAtrasados} / {linha.totalEntradas}</td><td className="p-2 text-right">{Number(linha.percentualAtrasados || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td></tr>)}</tbody>
          </table>
          {!linhas.length && <p className="p-4 text-center text-sm text-muted-foreground">Sem entradas no período.</p>}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2 items-center">
        <Button type="button" variant={periodo === "hoje" ? "default" : "outline"} onClick={() => setPeriodo("hoje")}>Hoje</Button>
        <Button type="button" variant={periodo === "ontem" ? "default" : "outline"} onClick={() => setPeriodo("ontem")}>Ontem</Button>
        <Button type="button" variant={periodo === "ultimos30dias" ? "default" : "outline"} onClick={() => setPeriodo("ultimos30dias")}>Últimos 30 dias</Button>
        <Button type="button" variant={periodo === "manual" ? "default" : "outline"} onClick={() => setPeriodo("manual")}>Período</Button>
        {periodo === "manual" && <><Input type="date" value={dataInicioManual} onChange={(evento) => setDataInicioManual(evento.target.value)} className="w-[165px]" /><Input type="date" value={dataFimManual} onChange={(evento) => setDataFimManual(evento.target.value)} className="w-[165px]" /></>}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {isAdmin && <Select value={emitenteId} onValueChange={setEmitenteId}><SelectTrigger className="w-[260px]"><SelectValue placeholder="Emitente" /></SelectTrigger><SelectContent><SelectItem value="__all__">Todos os emitentes</SelectItem>{(emitentes as any[] || []).map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.razaoSocial}</SelectItem>)}</SelectContent></Select>}
        <Select value={marketplaceId} onValueChange={setMarketplaceId}><SelectTrigger className="w-[220px]"><SelectValue placeholder="Loja" /></SelectTrigger><SelectContent><SelectItem value="__all__">Todas as lojas</SelectItem>{(marketplaces as any[] || []).map((marketplace: any) => <SelectItem key={marketplace.id} value={String(marketplace.id)}>{marketplace.nome}</SelectItem>)}</SelectContent></Select>
        <Select value={tipoEnvioId} onValueChange={setTipoEnvioId}><SelectTrigger className="w-[220px]"><SelectValue placeholder="Operação" /></SelectTrigger><SelectContent><SelectItem value="__all__">Todas as operações</SelectItem>{(tiposEnvio as any[] || []).filter((tipo: any) => tipo.nome !== "Devolução").map((tipo: any) => <SelectItem key={tipo.id} value={String(tipo.id)}>{tipo.nome}</SelectItem>)}</SelectContent></Select>
        <Select value={tipoTransporteId} onValueChange={setTipoTransporteId}><SelectTrigger className="w-[220px]"><SelectValue placeholder="Transportadora" /></SelectTrigger><SelectContent><SelectItem value="__all__">Todas as transportadoras</SelectItem>{(tiposTransporte as any[] || []).map((tipo: any) => <SelectItem key={tipo.id} value={String(tipo.id)}>{tipo.nome}</SelectItem>)}</SelectContent></Select>
        <Input value={busca} onChange={(evento) => setBusca(evento.target.value)} placeholder="Buscar código MECOM" className="w-[230px]" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium">Operação:</span>
        <Button type="button" size="sm" variant={tipoEnvioId === "__all__" ? "default" : "outline"} onClick={() => setTipoEnvioId("__all__")}>Todas</Button>
        {(tiposEnvio as any[] || []).filter((tipo: any) => tipo.nome !== "Devolução").map((tipo: any) => <Button key={tipo.id} type="button" size="sm" variant={tipoEnvioId === String(tipo.id) ? "default" : "outline"} onClick={() => setTipoEnvioId(String(tipo.id))}>{tipo.nome}</Button>)}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium">Preenchimento:</span>
        <Button type="button" size="sm" variant={motivoFiltro === "__all__" ? "default" : "outline"} onClick={() => setMotivoFiltro("__all__")}>Todos</Button>
        <Button type="button" size="sm" variant={motivoFiltro === "sem_motivo" ? "default" : "outline"} onClick={() => setMotivoFiltro("sem_motivo")}>Sem motivo</Button>
        {opcoesMotivo.map((opcao) => <Button key={opcao.value} type="button" size="sm" variant={motivoFiltro === opcao.value ? "default" : "outline"} onClick={() => setMotivoFiltro(opcao.value)}>{opcao.label}</Button>)}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {renderResumo("Percentual de atrasos — hoje", resumo?.hoje || [])}
        {renderResumo("Percentual de atrasos — ontem", resumo?.ontem || [])}
        {renderResumo("Percentual de atrasos — últimos 30 dias", resumo?.ultimos30Dias || [])}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Pedidos entrados e não expedidos no mesmo dia</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <table className="w-full min-w-[1380px] text-sm">
              <thead className="bg-muted"><tr><th className="p-2 text-left">Código MECOM</th><th className="p-2 text-left">Emitente</th><th className="p-2 text-left">Loja</th><th className="p-2 text-left">Tipo de entrega</th><th className="p-2 text-left">Transportadora</th><th className="p-2 text-left">Bip entrada</th><th className="p-2 text-left">Bip expedição</th><th className="p-2 text-left">Motivo</th><th className="p-2 text-left">Ação</th></tr></thead>
              <tbody>{(atrasos || []).map((pedido: any) => {
                const edicao = valorEdicao(pedido);
                return <tr key={pedido.id} className="border-t align-top"><td className="p-2 font-mono">{pedido.codigoBip}</td><td className="p-2">{pedido.emitenteNome || "-"}</td><td className="p-2">{pedido.lojaNome || "-"}</td><td className="p-2">{pedido.envioNome || "-"}</td><td className="p-2">{pedido.transporteNome || "-"}</td><td className="p-2 whitespace-nowrap">{pedido.bipEntradaEm ? new Date(pedido.bipEntradaEm).toLocaleString("pt-BR") : "-"}</td><td className="p-2 whitespace-nowrap">{pedido.bipExpedicaoEm ? new Date(pedido.bipExpedicaoEm).toLocaleString("pt-BR") : "Não expedido"}</td><td className="p-2"><div className="space-y-2"><Select value={edicao.motivo} onValueChange={(motivo) => atualizarEdicao(pedido, { motivo })}><SelectTrigger className="w-[260px]"><SelectValue placeholder="Selecione o motivo" /></SelectTrigger><SelectContent><SelectItem value="__none__">Selecione o motivo</SelectItem>{opcoesMotivo.map((opcao) => <SelectItem key={opcao.value} value={opcao.value}>{opcao.label}</SelectItem>)}</SelectContent></Select>{edicao.motivo === "outro" && <Input value={edicao.motivoOutro} onChange={(evento) => atualizarEdicao(pedido, { motivoOutro: evento.target.value })} placeholder="Descreva o motivo" className="w-[260px]" />}{pedido.atrasado ? <span className="text-xs text-muted-foreground">Salvo: {rotuloMotivo(pedido.motivoAtraso)}{pedido.motivoAtrasoOutro ? ` — ${pedido.motivoAtrasoOutro}` : ""}</span> : null}</div></td><td className="p-2"><Button type="button" size="sm" onClick={() => salvarPedido(pedido)} disabled={salvarAtraso.isPending}>{salvarAtraso.isPending ? "Salvando..." : "Salvar"}</Button></td></tr>;
              })}</tbody>
            </table>
            {!isLoading && !(atrasos || []).length && <p className="p-6 text-center text-sm text-muted-foreground">Nenhum pedido com atraso no período e filtros selecionados.</p>}
            {isLoading && <p className="p-6 text-center text-sm text-muted-foreground">Carregando...</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// BIP ENTRADA TAB
// ═══════════════════════════════════════════════════════════════════

function BipEntradaTab({ user }: { user: any }) {
  const [codigoBip, setCodigoBip] = useState("");
  const [marketplaceId, setMarketplaceId] = useState<string>(() => localStorage.getItem("mecom_entrada_marketplaceId") || "__none__");
  const [tipoEnvioId, setTipoEnvioId] = useState<string>(() => localStorage.getItem("mecom_entrada_tipoEnvioId") || "__none__");
  const [recentEntries, setRecentEntries] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: marketplaces } = trpc.mecom.marketplaces.list.useQuery();
  const { data: enviosFiltrados } = trpc.mecom.enviosByMarketplace.useQuery(
    { marketplaceId: Number(marketplaceId) },
    { enabled: marketplaceId !== "__none__" }
  );

  // Persist selections
  useEffect(() => { localStorage.setItem("mecom_entrada_marketplaceId", marketplaceId); }, [marketplaceId]);
  useEffect(() => { localStorage.setItem("mecom_entrada_tipoEnvioId", tipoEnvioId); }, [tipoEnvioId]);

  // Reset envio quando troca loja
  useEffect(() => { setTipoEnvioId("__none__"); }, [marketplaceId]);

  // Aggressive focus
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("select, button, [role='listbox'], [role='option'], [data-radix-select-viewport]")) return;
      forceFocus(inputRef);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, []);

  const bipMutation = trpc.mecom.bipEntrada.useMutation({
    onSuccess: (_data, variables) => {
      playSuccess();
      toast.success(`Entrada registrada: ${variables.codigoBip}`);
      setRecentEntries(prev => [{ codigoBip: variables.codigoBip, time: new Date() }, ...prev]);
      setCodigoBip("");
      forceFocus(inputRef);
    },
    onError: (err) => {
      playError();
      toast.error(err.message, { duration: 5000 });
      setCodigoBip("");
      forceFocus(inputRef);
    },
  });

  const handleBip = () => {
    if (!codigoBip.trim()) return;
    if (marketplaceId === "__none__") { toast.error("Selecione a Loja."); return; }
    if (tipoEnvioId === "__none__") { toast.error("Selecione o Envio."); return; }
    const normalized = normalizeCodBip(codigoBip);
    bipMutation.mutate({
      codigoBip: normalized,
      marketplaceId: Number(marketplaceId),
      tipoEnvioId: Number(tipoEnvioId),
    });
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-3">
        <Select value={marketplaceId} onValueChange={setMarketplaceId}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Loja" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Selecione a Loja...</SelectItem>
            {(marketplaces as any[] || []).map((m: any) => (
              <SelectItem key={m.id} value={String(m.id)}>{m.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tipoEnvioId} onValueChange={setTipoEnvioId} disabled={marketplaceId === "__none__"}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Envio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Selecione o Envio...</SelectItem>
            {(enviosFiltrados as any[] || []).map((t: any) => (
              <SelectItem key={t.tipoEnvioId} value={String(t.tipoEnvioId)}>{t.envioNome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={codigoBip}
          onChange={(e) => setCodigoBip(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleBip()}
          placeholder="Bipe ou digite o código (rastreio/chave de acesso)..."
          className="flex-1 font-mono"
          autoFocus
        />
        <Button onClick={handleBip} disabled={bipMutation.isLoading}>
          <Package className="w-4 h-4 mr-1" /> Registrar Entrada
        </Button>
      </div>

      {recentEntries.length > 0 && (
        <div className="border rounded-lg p-3">
          <h4 className="font-semibold mb-2">Entradas desta sessão ({recentEntries.length})</h4>
          <div className="max-h-[300px] overflow-auto space-y-1">
            {recentEntries.map((entry, i) => (
              <div key={i} className="flex justify-between text-sm font-mono bg-green-50 p-1 rounded">
                <span>{entry.codigoBip}</span>
                <span className="text-muted-foreground">{entry.time.toLocaleTimeString('pt-BR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// BIP GRAVAÇÃO TAB
// ═══════════════════════════════════════════════════════════════════

type PaginasEtiquetaOverlay = {
  etiqueta: HTMLCanvasElement;
  danfe: HTMLCanvasElement;
};

let workerPdfCompativelUrl: string | null = null;

function obterWorkerPdfCompativelUrl() {
  if (!workerPdfCompativelUrl) {
    const workerPdfAbsolutoUrl = new URL(pdfWorkerUrl, window.location.origin).href;
    const codigoWorker = `
      if (typeof Uint8Array.prototype.toHex !== "function") {
        Object.defineProperty(Uint8Array.prototype, "toHex", {
          value: function () { let resultado = ""; for (let indice = 0; indice < this.length; indice++) resultado += this[indice].toString(16).padStart(2, "0"); return resultado; },
          configurable: true,
          writable: true,
        });
      }
      import ${JSON.stringify(workerPdfAbsolutoUrl)};
    `;
    workerPdfCompativelUrl = URL.createObjectURL(new Blob([codigoWorker], { type: "text/javascript" }));
  }
  return workerPdfCompativelUrl;
}

async function carregarPaginasEtiquetaOverlay(arquivoUrl: string): Promise<PaginasEtiquetaOverlay> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = obterWorkerPdfCompativelUrl();
  const resposta = await fetch(arquivoUrl, { credentials: "include" });
  if (!resposta.ok) throw new Error("Não foi possível carregar o PDF da etiqueta.");
  const documento = await pdfjs.getDocument({ data: await resposta.arrayBuffer() }).promise;
  if (documento.numPages !== 2) throw new Error("O PDF individual da etiqueta não possui duas páginas.");
  const renderizarPagina = async (numero: number) => {
    const pagina = await documento.getPage(numero);
    const viewport = pagina.getViewport({ scale: 1 });
    const escala = 556 / viewport.height;
    const viewportEscalado = pagina.getViewport({ scale: escala });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewportEscalado.width);
    canvas.height = Math.ceil(viewportEscalado.height);
    const contexto = canvas.getContext("2d");
    if (!contexto) throw new Error("Não foi possível preparar a imagem da etiqueta.");
    await pagina.render({ canvasContext: contexto, viewport: viewportEscalado, canvas }).promise;
    return canvas;
  };
  const [etiqueta, danfe] = await Promise.all([renderizarPagina(1), renderizarPagina(2)]);
  await documento.destroy();
  return { etiqueta, danfe };
}

async function aguardarVideoPronto(video: HTMLVideoElement) {
  if (video.readyState >= 2 && video.videoWidth > 0) return;
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      video.removeEventListener("loadeddata", pronto);
      reject(new Error("A câmera não disponibilizou imagem para iniciar a gravação."));
    }, 5000);
    const pronto = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    video.addEventListener("loadeddata", pronto, { once: true });
  });
}

function falarItensEntradaExterna(valor: unknown) {
  if (!("speechSynthesis" in window) || typeof SpeechSynthesisUtterance === "undefined") return;
  window.speechSynthesis.cancel();
  let itens = valor;
  if (typeof itens === "string") {
    try { itens = JSON.parse(itens); } catch { return; }
  }
  if (!Array.isArray(itens)) return;
  const itensValidos = itens.map((item: any) => {
    const descricao = String(item?.descricao || "").trim();
    const primeiraPalavra = descricao.split(/\s+/)[0] || "";
    const quantidade = Number(String(item?.quantidade || "0").replace(",", "."));
    const sku = String(item?.sku || "").trim();
    return { primeiraPalavra, quantidade, sku };
  }).filter((item) => item.primeiraPalavra && Number.isFinite(item.quantidade) && item.quantidade > 0);
  if (!itensValidos.length) return;
  const maisDeUmSku = new Set(itensValidos.map((item) => item.sku).filter(Boolean)).size > 1;
  const maisDeUmaUnidade = itensValidos.reduce((total, item) => total + item.quantidade, 0) > 1;
  const alerta = maisDeUmSku || maisDeUmaUnidade ? "Atenção, pedido com mais de um item. " : "";
  const itensFalados = itensValidos.map((item) => `${item.primeiraPalavra} ${item.quantidade}`).join(", ");
  const fala = new SpeechSynthesisUtterance(`${alerta}${itensFalados}.`);
  fala.lang = "pt-BR";
  window.speechSynthesis.speak(fala);
}

function itensPainelEntradaExterna(valor: unknown) {
  let itens = valor;
  if (typeof itens === "string") {
    try { itens = JSON.parse(itens); } catch { return []; }
  }
  if (!Array.isArray(itens)) return [];
  return itens.map((item: any) => ({
    sku: String(item?.sku || "").trim(),
    descricao: String(item?.descricao || "").trim(),
    quantidade: String(item?.quantidade || "").trim(),
  })).filter((item) => item.sku || item.descricao || item.quantidade);
}

function BipGravacaoTab({ user }: { user: any }) {
  const [pontoOperacionalId, setPontoOperacionalId] = useState<string>(() => localStorage.getItem("mecom_gravacao_pontoId") || "__none__");
  const [codigoBip, setCodigoBip] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [currentBip, setCurrentBip] = useState<string | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recentGravacoes, setRecentGravacoes] = useState<any[]>([]);
  const [recordedInSession, setRecordedInSession] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [itensAtuais, setItensAtuais] = useState<Array<{ sku: string; descricao: string; quantidade: string }>>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const composicaoStreamRef = useRef<MediaStream | null>(null);
  const composicaoTimerRef = useRef<number | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const recordingStartRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: pontosOperacionais } = trpc.pontosOperacionais.list.useQuery();
  const { data: pontosOperacionaisComNome } = trpc.pontosOperacionais.comNome.list.useQuery();
  const { data: pontoStats } = trpc.mecom.pontoStats.useQuery(
    { pontoOperacionalId: Number(pontoOperacionalId) },
    { enabled: pontoOperacionalId !== "__none__" }
  );
  const bipGravacaoMutation = trpc.mecom.bipGravacao.useMutation({
    onSuccess: (_data, variables) => {
      playSuccess();
      toast.success(`Gravação salva: ${variables.codigoBip}`);
      setRecentGravacoes(prev => [{ codigoBip: variables.codigoBip, time: new Date(), duration: variables.duration }, ...prev]);
    },
    onError: (err) => {
      playError();
      toast.error(err.message, { duration: 5000 });
    },
  });

  // Persist ponto operacional
  useEffect(() => { localStorage.setItem("mecom_gravacao_pontoId", pontoOperacionalId); }, [pontoOperacionalId]);

  // Timer for recording duration display
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingSeconds(Math.floor((Date.now() - recordingStartRef.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setRecordingSeconds(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording]);

  // Aggressive focus
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("select, button, video, [role='listbox'], [role='option'], [data-radix-select-viewport]")) return;
      forceFocus(inputRef);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, []);

  // Initialize camera once (persistent stream)
  const initCamera = useCallback(async () => {
    if (streamRef.current) return; // Already initialized
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }
      setIsCameraOn(true);
    } catch (err) {
      playError();
      toast.error("Erro ao acessar câmera. Verifique permissões.");
    }
  }, []);

  const pararComposicao = useCallback(() => {
    if (composicaoTimerRef.current !== null) {
      window.clearInterval(composicaoTimerRef.current);
      composicaoTimerRef.current = null;
    }
    if (composicaoStreamRef.current) {
      composicaoStreamRef.current.getTracks().forEach(track => track.stop());
      composicaoStreamRef.current = null;
    }
  }, []);

  // Start a new MediaRecorder on the existing stream
  const startNewRecorder = useCallback(async (etiquetaArquivoUrl?: string | null) => {
    if (!streamRef.current) return;
    pararComposicao();
    let streamGravacao = streamRef.current;
    if (etiquetaArquivoUrl) {
      const paginas = await carregarPaginasEtiquetaOverlay(etiquetaArquivoUrl);
      const camera = videoPreviewRef.current;
      if (!camera) throw new Error("Não foi possível preparar a composição do vídeo.");
      await aguardarVideoPronto(camera);
      const configuracoes = streamRef.current.getVideoTracks()[0]?.getSettings();
      const largura = Number(configuracoes?.width || camera?.videoWidth || 1280);
      const altura = Number(configuracoes?.height || camera?.videoHeight || 720);
      const canvas = document.createElement("canvas");
      canvas.width = largura;
      canvas.height = altura;
      const contexto = canvas.getContext("2d");
      if (!contexto) throw new Error("Não foi possível preparar a composição do vídeo.");
      const desenhar = () => {
        contexto.drawImage(camera, 0, 0, largura, altura);
        const alturaPainel = Math.round(altura * 286 / 720);
        const margem = Math.round(largura * 20 / 1280);
        const preenchimento = Math.max(2, Math.round(altura * 4 / 720));
        const alturaPagina = alturaPainel - preenchimento * 2;
        const larguraEtiqueta = paginas.etiqueta.width * alturaPagina / paginas.etiqueta.height;
        const larguraDanfe = paginas.danfe.width * alturaPagina / paginas.danfe.height;
        const larguraPainel = Math.round(larguraEtiqueta + larguraDanfe + preenchimento * 2);
        const x = largura - larguraPainel - margem;
        const y = altura - alturaPainel - margem;
        contexto.fillStyle = "white";
        contexto.fillRect(x, y, larguraPainel, alturaPainel);
        contexto.drawImage(paginas.etiqueta, x + preenchimento, y + preenchimento, larguraEtiqueta, alturaPagina);
        contexto.drawImage(paginas.danfe, x + preenchimento + larguraEtiqueta, y + preenchimento, larguraDanfe, alturaPagina);
        contexto.strokeStyle = "black";
        contexto.lineWidth = Math.max(2, Math.round(altura * 3 / 720));
        contexto.strokeRect(x, y, larguraPainel, alturaPainel);
      };
      desenhar();
      composicaoTimerRef.current = window.setInterval(desenhar, 1000 / 30);
      const streamComposto = canvas.captureStream(30);
      composicaoStreamRef.current = streamComposto;
      streamGravacao = streamComposto;
    }
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm';
    const mediaRecorder = new MediaRecorder(streamGravacao, {
      mimeType,
      videoBitsPerSecond: 1500000, // 1.5 Mbps
    });
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    mediaRecorder.start(1000);
    mediaRecorderRef.current = mediaRecorder;
    recordingStartRef.current = Date.now();
    setIsRecording(true);
  }, [pararComposicao]);

  // Stop current recorder and upload
  const stopAndSave = useCallback(async (bipCode: string): Promise<{ url: string; duration: number; fileSize: number; startedAt: string }> => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        reject('Nenhuma gravação ativa');
        return;
      }
      // Define onstop BEFORE calling stop() — critical
      recorder.onstop = async () => {
        const recordedChunks = [...chunksRef.current]; // Immediate copy
        chunksRef.current = []; // Clear for next recording
        pararComposicao();

        const videoBlob = new Blob(recordedChunks, { type: 'video/webm' });
        const duration = Math.floor((Date.now() - recordingStartRef.current) / 1000);
        const fileSize = videoBlob.size;
        const startedAt = new Date(recordingStartRef.current).toISOString();
        const nomeArquivo = `mecom_${bipCode}_${Date.now()}.webm`;

        const formData = new FormData();
        formData.append('file', videoBlob, nomeArquivo);

        try {
          setIsUploading(true);
          const url = await uploadWithRetry(formData, 3);
          setIsUploading(false);
          resolve({ url, duration, fileSize, startedAt });
        } catch (err) {
          setIsUploading(false);
          baixarVideoFalhado(videoBlob, nomeArquivo);
          reject('Upload falhou após 3 tentativas. O vídeo foi baixado neste computador.');
        }
      };
      recorder.stop();
      setIsRecording(false);
    });
  }, [pararComposicao]);

  // Stop camera completely
  const stopCamera = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    pararComposicao();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsRecording(false);
    setCurrentBip(null);
  }, [pararComposicao]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopCamera(); };
  }, [stopCamera]);

  const handleBip = async () => {
    if (!codigoBip.trim()) return;
    if (pontoOperacionalId === "__none__") {
      playError();
      toast.error("Selecione um ponto operacional antes de gravar.", { duration: 5000 });
      return;
    }
    const normalized = normalizeCodBip(codigoBip);
    let etiquetaArquivoUrl: string | null = null;
    let itensEntradaExternaJson: unknown = null;
    setCodigoBip("");

    // Duplicate check (session level)
    if (recordedInSession.has(normalized)) {
      playError();
      toast.error(`Código ${normalized} já foi gravado nesta sessão!`, { duration: 5000 });
      forceFocus(inputRef);
      return;
    }

    // Validação prévia: verificar se pedido existe antes de gravar
    try {
      const checkRes = await fetch(`/api/trpc/mecom.checkPedido?input=${encodeURIComponent(JSON.stringify({ json: { codigoBip: normalized } }))}`, { credentials: 'include' });
      const checkJson = await checkRes.json();
      const check = checkJson?.result?.data?.json;
      if (!check.exists) {
        playError();
        toast.error(`Pedido não encontrado: ${normalized}. Realize a entrada primeiro.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'gravado') {
        playError();
        toast.error(`Pedido ${normalized} já foi gravado.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'expedido') {
        playError();
        toast.error(`Pedido ${normalized} já foi expedido. Não é possível gravar.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'devolvido') {
        playError();
        toast.error(`Pedido ${normalized} já foi devolvido. Não é possível gravar.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      etiquetaArquivoUrl = check.etiquetaArquivoUrl || null;
      itensEntradaExternaJson = check.itensEntradaExternaJson || null;
    } catch (err: any) {
      playError();
      toast.error(`Erro ao verificar pedido: ${err.message}`, { duration: 5000 });
      forceFocus(inputRef);
      return;
    }

    // If already recording, stop previous and save
    if (isRecording && currentBip) {
      try {
        const { url, duration, fileSize, startedAt } = await stopAndSave(currentBip);
        bipGravacaoMutation.mutate({
          codigoBip: currentBip,
          pontoOperacionalId: Number(pontoOperacionalId),
          videoUrl: url,
          duration,
          fileSize,
          startedAt,
        });
        setRecordedInSession(prev => new Set(prev).add(currentBip));
      } catch (err) {
        playError();
        toast.error(`Erro ao salvar gravação de ${currentBip}: ${err}`);
      }
    }

    // Initialize camera if not yet
    if (!streamRef.current) {
      await initCamera();
    }

    // Start new recording
    try {
      await startNewRecorder(etiquetaArquivoUrl);
      setCurrentBip(normalized);
      setItensAtuais(itensPainelEntradaExterna(itensEntradaExternaJson));
      if (mediaRecorderRef.current?.state === "recording") falarItensEntradaExterna(itensEntradaExternaJson);
    } catch (err: any) {
      playError();
      toast.error(err.message || "Não foi possível preparar a etiqueta no vídeo.", { duration: 5000 });
    }
    forceFocus(inputRef);
  };

  const handleStop = async () => {
    if (!isRecording || !currentBip) return;
    if (pontoOperacionalId === "__none__") {
      playError();
      toast.error("Ponto operacional não selecionado.");
      return;
    }
    try {
      const { url, duration, fileSize, startedAt } = await stopAndSave(currentBip);
      bipGravacaoMutation.mutate({
        codigoBip: currentBip,
        pontoOperacionalId: Number(pontoOperacionalId),
        videoUrl: url,
        duration,
        fileSize,
        startedAt,
      });
      setRecordedInSession(prev => new Set(prev).add(currentBip));
    } catch (err) {
      playError();
      toast.error(`Erro ao salvar: ${err}`);
    }
    setCurrentBip(null);
    // Camera stays on — only recorder stops
    forceFocus(inputRef);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex gap-3 items-center">
        <span className="font-medium">Ponto Operacional:</span>
        <Select value={pontoOperacionalId} onValueChange={setPontoOperacionalId}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Selecione...</SelectItem>
            {(pontosOperacionaisComNome as any[] || pontosOperacionais as any[] || []).map((p: any) => (
              <SelectItem key={p.id} value={String(p.id)}>{p.nome ? `${p.nome} — ${p.tipo} #${p.id}` : `${p.tipo} - #${p.id}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isCameraOn && (
          <Button variant="outline" onClick={initCamera}>Ligar Câmera</Button>
        )}
        {isCameraOn && !isRecording && (
          <Button variant="outline" onClick={stopCamera}>Desligar Câmera</Button>
        )}
      </div>

      {pontoStats && pontoOperacionalId !== "__none__" && (
        <div className="flex gap-4 text-sm">
          <span className="bg-muted px-2 py-1 rounded">Hoje: <strong>{pontoStats.hoje}</strong></span>
          <span className="bg-muted px-2 py-1 rounded">Mês: <strong>{pontoStats.mes}</strong></span>
          <span className="bg-muted px-2 py-1 rounded">Média/dia: <strong>{pontoStats.media}</strong></span>
        </div>
      )}

      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={codigoBip}
          onChange={(e) => setCodigoBip(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleBip()}
          placeholder="Bipe o código para iniciar gravação..."
          className="flex-1 font-mono"
          autoFocus
        />
        <Button onClick={handleBip} disabled={isUploading}>
          <Video className="w-4 h-4 mr-1" /> Bipar
        </Button>
        {isRecording && (
          <Button onClick={handleStop} variant="destructive" disabled={isUploading}>
            <StopCircle className="w-4 h-4 mr-1" /> Parar Gravação
          </Button>
        )}
      </div>

      {isRecording && currentBip && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium">Gravando: <span className="font-mono">{currentBip}</span></span>
          <span className="text-sm font-mono text-red-600 ml-auto">{formatTime(recordingSeconds)}</span>
        </div>
      )}

      <MecomRecordingCount isRecording={isRecording} codigoBip={currentBip} videoRef={videoPreviewRef} />

      {isUploading && (
        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
          <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium">Enviando vídeo...</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,32rem)_minmax(0,1fr)]">
      <video ref={videoPreviewRef} className="w-full max-w-lg rounded border" muted playsInline style={{ display: isCameraOn ? 'block' : 'none' }} />

      {isCameraOn && (
        <div className="min-w-0 overflow-hidden rounded border">
          <div className="grid grid-cols-[minmax(9rem,0.8fr)_minmax(14rem,2fr)_8rem] gap-3 border-b bg-muted/50 px-4 py-3 text-base font-bold">
            <span>COD</span><span>DESCRIÇÃO</span><span className="text-center">QUANTIDADE</span>
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {itensAtuais.map((item, indice) => (
              <div key={`${item.sku}-${indice}`} className="grid grid-cols-[minmax(9rem,0.8fr)_minmax(14rem,2fr)_8rem] gap-3 border-b px-4 py-4 text-lg last:border-b-0">
                <span className="break-words font-mono font-semibold">{item.sku || "-"}</span>
                <span className="break-words font-semibold">{item.descricao || "-"}</span>
                <span className="text-center text-xl font-bold">{item.quantidade || "-"}</span>
              </div>
            ))}
            {!itensAtuais.length && <div className="p-8 text-center text-base text-muted-foreground">Nenhum item carregado.</div>}
          </div>
        </div>
      )}
      </div>

      {recentGravacoes.length > 0 && (
        <div className="border rounded-lg p-3">
          <h4 className="font-semibold mb-2">Gravações desta sessão ({recentGravacoes.length})</h4>
          <div className="max-h-[200px] overflow-auto space-y-1">
            {recentGravacoes.map((g, i) => (
              <div key={i} className="flex justify-between text-sm font-mono bg-yellow-50 p-1 rounded">
                <span>{g.codigoBip}</span>
                <span className="text-muted-foreground">{g.duration ? `${g.duration}s` : ''} — {g.time.toLocaleTimeString('pt-BR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// BIP EXPEDIÇÃO TAB
// ═══════════════════════════════════════════════════════════════════

function BipExpedicaoTab({ user }: { user: any }) {
  const [codigoBip, setCodigoBip] = useState("");
  const [recentExpedicoes, setRecentExpedicoes] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Aggressive focus
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("select, button, [role='listbox'], [role='option'], [data-radix-select-viewport]")) return;
      forceFocus(inputRef);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, []);

  const bipMutation = trpc.mecom.bipExpedicao.useMutation({
    onSuccess: (_data, variables) => {
      playSuccess();
      toast.success(`Expedição registrada: ${variables.codigoBip}`);
      setRecentExpedicoes(prev => [{ codigoBip: variables.codigoBip, time: new Date() }, ...prev]);
      setCodigoBip("");
      forceFocus(inputRef);
    },
    onError: (err) => {
      playError();
      toast.error(err.message, { duration: 5000 });
      setCodigoBip("");
      forceFocus(inputRef);
    },
  });

  const handleBip = async () => {
    if (!codigoBip.trim()) return;
    const normalized = normalizeCodBip(codigoBip);
    setCodigoBip("");

    // Validação prévia: verificar se pedido existe e status permite expedição
    try {
      const checkRes = await fetch(`/api/trpc/mecom.checkPedido?input=${encodeURIComponent(JSON.stringify({ json: { codigoBip: normalized } }))}`, { credentials: 'include' });
      const checkJson = await checkRes.json();
      const check = checkJson?.result?.data?.json;
      if (!check?.exists) {
        playError();
        toast.error(`Pedido não encontrado: ${normalized}. Realize a entrada primeiro.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'expedido') {
        playError();
        toast.error(`Pedido ${normalized} já foi expedido.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'devolvido') {
        playError();
        toast.error(`Pedido ${normalized} já foi devolvido. Não é possível expedir.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
    } catch (err: any) {
      playError();
      toast.error(`Erro ao verificar pedido: ${err.message}`, { duration: 5000 });
      forceFocus(inputRef);
      return;
    }

    bipMutation.mutate({ codigoBip: normalized });
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={codigoBip}
          onChange={(e) => setCodigoBip(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleBip()}
          placeholder="Bipe o código para registrar expedição..."
          className="flex-1 font-mono"
          autoFocus
        />
        <Button onClick={handleBip} disabled={bipMutation.isLoading}>
          <Truck className="w-4 h-4 mr-1" /> Registrar Expedição
        </Button>
      </div>

      {recentExpedicoes.length > 0 && (
        <div className="border rounded-lg p-3">
          <h4 className="font-semibold mb-2">Expedições desta sessão ({recentExpedicoes.length})</h4>
          <div className="max-h-[300px] overflow-auto space-y-1">
            {recentExpedicoes.map((e, i) => (
              <div key={i} className="flex justify-between text-sm font-mono bg-green-50 p-1 rounded">
                <span>{e.codigoBip}</span>
                <span className="text-muted-foreground">{e.time.toLocaleTimeString('pt-BR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DEVOLUÇÃO TAB (mesma lógica da gravação)
// ═══════════════════════════════════════════════════════════════════

function DevolucaoTab({ user }: { user: any }) {
  const [pontoOperacionalId, setPontoOperacionalId] = useState<string>(() => localStorage.getItem("mecom_devolucao_pontoId") || "__none__");
  const [codigoBip, setCodigoBip] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [currentBip, setCurrentBip] = useState<string | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recentDevolucoes, setRecentDevolucoes] = useState<any[]>([]);
  const [recordedInSession, setRecordedInSession] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const recordingStartRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: pontosOperacionais } = trpc.pontosOperacionais.list.useQuery();
  const { data: pontosOperacionaisComNome } = trpc.pontosOperacionais.comNome.list.useQuery();
  const { data: pontoStats } = trpc.mecom.pontoStats.useQuery(
    { pontoOperacionalId: Number(pontoOperacionalId) },
    { enabled: pontoOperacionalId !== "__none__" }
  );
  const bipDevolucaoMutation = trpc.mecom.bipDevolucao.useMutation({
    onSuccess: (_data, variables) => {
      playSuccess();
      toast.success(`Devolução registrada: ${variables.codigoBip}`);
      setRecentDevolucoes(prev => [{ codigoBip: variables.codigoBip, time: new Date(), duration: variables.duration }, ...prev]);
    },
    onError: (err) => {
      playError();
      toast.error(err.message, { duration: 5000 });
    },
  });

  // Persist ponto operacional
  useEffect(() => { localStorage.setItem("mecom_devolucao_pontoId", pontoOperacionalId); }, [pontoOperacionalId]);

  // Timer
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingSeconds(Math.floor((Date.now() - recordingStartRef.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setRecordingSeconds(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording]);

  // Aggressive focus
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("select, button, video, [role='listbox'], [role='option'], [data-radix-select-viewport]")) return;
      forceFocus(inputRef);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, []);

  const initCamera = useCallback(async () => {
    if (streamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }
      setIsCameraOn(true);
    } catch (err) {
      playError();
      toast.error("Erro ao acessar câmera. Verifique permissões.");
    }
  }, []);

  const startNewRecorder = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm';
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType,
      videoBitsPerSecond: 1500000, // 1.5 Mbps
    });
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    recordingStartRef.current = Date.now();
    setIsRecording(true);
  }, []);

  const stopAndSave = useCallback(async (bipCode: string): Promise<{ url: string; duration: number; fileSize: number; startedAt: string }> => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        reject('Nenhuma gravação ativa');
        return;
      }
      recorder.onstop = async () => {
        const recordedChunks = [...chunksRef.current];
        chunksRef.current = [];
        const videoBlob = new Blob(recordedChunks, { type: 'video/webm' });
        const duration = Math.floor((Date.now() - recordingStartRef.current) / 1000);
        const fileSize = videoBlob.size;
        const startedAt = new Date(recordingStartRef.current).toISOString();

        try {
          setIsUploading(true);
          await validarVideoWebm(videoBlob);
          const formData = new FormData();
          formData.append('file', videoBlob, `mecom_dev_${bipCode}_${Date.now()}.webm`);
          const url = await uploadWithRetry(formData, 3);
          await confirmarVideoPublicado(url, fileSize);
          setIsUploading(false);
          resolve({ url, duration, fileSize, startedAt });
        } catch (err) {
          setIsUploading(false);
          reject('Upload falhou após 3 tentativas');
        }
      };
      recorder.stop();
      setIsRecording(false);
    });
  }, []);

  const stopCamera = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsRecording(false);
    setCurrentBip(null);
  }, []);

  useEffect(() => {
    return () => { stopCamera(); };
  }, [stopCamera]);

  const handleBip = async () => {
    if (!codigoBip.trim()) return;
    if (pontoOperacionalId === "__none__") {
      playError();
      toast.error("Selecione um ponto operacional antes de gravar.", { duration: 5000 });
      return;
    }
    const normalized = normalizeCodBip(codigoBip);
    setCodigoBip("");

    if (recordedInSession.has(normalized)) {
      playError();
      toast.error(`Código ${normalized} já foi registrado como devolução nesta sessão!`, { duration: 5000 });
      forceFocus(inputRef);
      return;
    }

    // Validação prévia: verificar se pedido existe antes de devolver
    try {
      const checkRes = await fetch(`/api/trpc/mecom.checkPedido?input=${encodeURIComponent(JSON.stringify({ json: { codigoBip: normalized } }))}`, { credentials: 'include' });
      const checkJson = await checkRes.json();
      const check = checkJson?.result?.data?.json;
      if (!check.exists) {
        playError();
        toast.error(`Pedido não encontrado: ${normalized}. Realize a entrada primeiro.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'devolvido') {
        playError();
        toast.error(`Pedido ${normalized} já foi devolvido.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
    } catch (err: any) {
      playError();
      toast.error(`Erro ao verificar pedido: ${err.message}`, { duration: 5000 });
      forceFocus(inputRef);
      return;
    }

    if (isRecording && currentBip) {
      try {
        const { url, duration, fileSize, startedAt } = await stopAndSave(currentBip);
        bipDevolucaoMutation.mutate({
          codigoBip: currentBip,
          pontoOperacionalId: Number(pontoOperacionalId),
          videoUrl: url,
          duration,
          fileSize,
          startedAt,
        });
        setRecordedInSession(prev => new Set(prev).add(currentBip));
      } catch (err) {
        playError();
        toast.error(`Erro ao salvar gravação de ${currentBip}: ${err}`);
      }
    }

    if (!streamRef.current) {
      await initCamera();
    }

    setCurrentBip(normalized);
    startNewRecorder();
    forceFocus(inputRef);
  };

  const handleStop = async () => {
    if (!isRecording || !currentBip) return;
    if (pontoOperacionalId === "__none__") {
      playError();
      toast.error("Ponto operacional não selecionado.");
      return;
    }
    try {
      const { url, duration, fileSize, startedAt } = await stopAndSave(currentBip);
      bipDevolucaoMutation.mutate({
        codigoBip: currentBip,
        pontoOperacionalId: Number(pontoOperacionalId),
        videoUrl: url,
        duration,
        fileSize,
        startedAt,
      });
      setRecordedInSession(prev => new Set(prev).add(currentBip));
    } catch (err) {
      playError();
      toast.error(`Erro ao salvar: ${err}`);
    }
    setCurrentBip(null);
    forceFocus(inputRef);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex gap-3 items-center">
        <span className="font-medium">Ponto Operacional:</span>
        <Select value={pontoOperacionalId} onValueChange={setPontoOperacionalId}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Selecione...</SelectItem>
            {(pontosOperacionaisComNome as any[] || pontosOperacionais as any[] || []).map((p: any) => (
              <SelectItem key={p.id} value={String(p.id)}>{p.nome ? `${p.nome} — ${p.tipo} #${p.id}` : `${p.tipo} - #${p.id}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isCameraOn && (
          <Button variant="outline" onClick={initCamera}>Ligar Câmera</Button>
        )}
        {isCameraOn && !isRecording && (
          <Button variant="outline" onClick={stopCamera}>Desligar Câmera</Button>
        )}
      </div>

      {pontoStats && pontoOperacionalId !== "__none__" && (
        <div className="flex gap-4 text-sm">
          <span className="bg-muted px-2 py-1 rounded">Hoje: <strong>{pontoStats.hoje}</strong></span>
          <span className="bg-muted px-2 py-1 rounded">Mês: <strong>{pontoStats.mes}</strong></span>
          <span className="bg-muted px-2 py-1 rounded">Média/dia: <strong>{pontoStats.media}</strong></span>
        </div>
      )}

      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={codigoBip}
          onChange={(e) => setCodigoBip(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleBip()}
          placeholder="Bipe o código para registrar devolução..."
          className="flex-1 font-mono"
          autoFocus
        />
        <Button onClick={handleBip} disabled={isUploading}>
          <RotateCcw className="w-4 h-4 mr-1" /> Bipar
        </Button>
        {isRecording && (
          <Button onClick={handleStop} variant="destructive" disabled={isUploading}>
            <StopCircle className="w-4 h-4 mr-1" /> Parar Gravação
          </Button>
        )}
      </div>

      {isRecording && currentBip && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium">Gravando devolução: <span className="font-mono">{currentBip}</span></span>
          <span className="text-sm font-mono text-red-600 ml-auto">{formatTime(recordingSeconds)}</span>
        </div>
      )}

      {isUploading && (
        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
          <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium">Enviando vídeo...</span>
        </div>
      )}

      <video ref={videoPreviewRef} className="w-full max-w-lg rounded border" muted playsInline style={{ display: isCameraOn ? 'block' : 'none' }} />

      {recentDevolucoes.length > 0 && (
        <div className="border rounded-lg p-3">
          <h4 className="font-semibold mb-2">Devoluções desta sessão ({recentDevolucoes.length})</h4>
          <div className="max-h-[200px] overflow-auto space-y-1">
            {recentDevolucoes.map((d, i) => (
              <div key={i} className="flex justify-between text-sm font-mono bg-red-50 p-1 rounded">
                <span>{d.codigoBip}</span>
                <span className="text-muted-foreground">{d.duration ? `${d.duration}s` : ''} — {d.time.toLocaleTimeString('pt-BR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CADASTROS TAB
// ═══════════════════════════════════════════════════════════════════

function CadastrosTab({ user }: { user: any }) {
  const [subTab, setSubTab] = useState("transporte");
  return (
    <div className="mt-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="transporte">Tipos de Transporte</TabsTrigger>
          <TabsTrigger value="envio">Tipos de Envio</TabsTrigger>
          <TabsTrigger value="vinculo">Vínculos</TabsTrigger>
          <TabsTrigger value="corte">Horas de corte</TabsTrigger>
          <TabsTrigger value="marketplace">Marketplaces/Lojas</TabsTrigger>
        </TabsList>
        <TabsContent value="transporte"><CrudTransporte user={user} /></TabsContent>
        <TabsContent value="envio"><CrudEnvio user={user} /></TabsContent>
        <TabsContent value="vinculo"><CrudVinculo user={user} /></TabsContent>
        <TabsContent value="corte"><CrudTempoRealCortes /></TabsContent>
        <TabsContent value="marketplace"><CrudMarketplace user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

function CrudTransporte({ user }: { user: any }) {
  const [nome, setNome] = useState("");
  const utils = trpc.useUtils();
  const { data: items } = trpc.mecom.tiposTransporte.list.useQuery();
  const createMut = trpc.mecom.tiposTransporte.create.useMutation({ onSuccess: () => { utils.mecom.tiposTransporte.list.invalidate(); setNome(""); toast.success("Criado!"); } });
  const deleteMut = trpc.mecom.tiposTransporte.delete.useMutation({ onSuccess: () => { utils.mecom.tiposTransporte.list.invalidate(); toast.success("Removido!"); } });

  const handleCreate = () => {
    if (!nome.trim()) return;
    createMut.mutate({ nome: nome.trim() });
  };

  return (
    <div className="space-y-3 mt-3">
      <div className="flex gap-2">
        <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do tipo de transporte" className="max-w-sm" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
        <Button onClick={handleCreate} disabled={createMut.isLoading}><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
      </div>
      <div className="border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted"><tr><th className="p-2 text-left">Nome</th><th className="p-2 text-right">Ações</th></tr></thead>
          <tbody>
            {(items as any[] || []).map((item: any) => (
              <tr key={item.id} className="border-t"><td className="p-2">{item.nome}</td><td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => deleteMut.mutate({ id: item.id })}><Trash2 className="w-4 h-4 text-red-500" /></Button></td></tr>
            ))}
            {(!items || (items as any[]).length === 0) && <tr><td colSpan={2} className="p-4 text-center text-muted-foreground">Nenhum cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CrudEnvio({ user }: { user: any }) {
  const [nome, setNome] = useState("");
  const utils = trpc.useUtils();
  const { data: items } = trpc.mecom.tiposEnvio.list.useQuery();
  const createMut = trpc.mecom.tiposEnvio.create.useMutation({ onSuccess: () => { utils.mecom.tiposEnvio.list.invalidate(); setNome(""); toast.success("Criado!"); } });
  const deleteMut = trpc.mecom.tiposEnvio.delete.useMutation({ onSuccess: () => { utils.mecom.tiposEnvio.list.invalidate(); toast.success("Removido!"); } });

  const handleCreate = () => {
    if (!nome.trim()) return;
    createMut.mutate({ nome: nome.trim() });
  };

  return (
    <div className="space-y-3 mt-3">
      <div className="flex gap-2">
        <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do tipo de envio" className="max-w-sm" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
        <Button onClick={handleCreate} disabled={createMut.isLoading}><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
      </div>
      <div className="border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted"><tr><th className="p-2 text-left">Nome</th><th className="p-2 text-right">Ações</th></tr></thead>
          <tbody>
            {(items as any[] || []).map((item: any) => (
              <tr key={item.id} className="border-t"><td className="p-2">{item.nome}</td><td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => deleteMut.mutate({ id: item.id })}><Trash2 className="w-4 h-4 text-red-500" /></Button></td></tr>
            ))}
            {(!items || (items as any[]).length === 0) && <tr><td colSpan={2} className="p-4 text-center text-muted-foreground">Nenhum cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CrudTempoRealCortes() {
  const [horarios, setHorarios] = useState<Record<string, string>>({});
  const utils = trpc.useUtils();
  const { data: cortes } = trpc.mecom.tempoReal.cortes.list.useQuery();
  const salvarMut = trpc.mecom.tempoReal.cortes.salvar.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.mecom.tempoReal.cortes.list.invalidate(),
        utils.mecom.tempoReal.list.invalidate(),
      ]);
      toast.success("Hora de corte salva.");
    },
    onError: (erro: any) => toast.error(erro.message || "Não foi possível salvar a hora de corte."),
  });

  return (
    <div className="space-y-3 mt-3">
      <p className="text-sm text-muted-foreground">Uma hora de corte única para cada combinação de tipo de envio e transportadora.</p>
      <div className="border rounded-lg overflow-auto">
        <table className="w-full min-w-[620px] text-sm">
          <thead className="bg-muted"><tr><th className="p-2 text-left">Tipo de envio</th><th className="p-2 text-left">Transportadora</th><th className="p-2 text-left">Hora de corte</th><th className="p-2 text-right">Ação</th></tr></thead>
          <tbody>{(cortes as any[] || []).map((corte: any) => {
            const chave = `${corte.tipoEnvioId}-${corte.tipoTransporteId}`;
            const horaCorte = horarios[chave] ?? corte.horaCorte ?? "";
            return <tr key={chave} className="border-t"><td className="p-2">{corte.envioNome}</td><td className="p-2">{corte.transporteNome}</td><td className="p-2"><Input type="time" value={horaCorte} onChange={(evento) => setHorarios((atuais) => ({ ...atuais, [chave]: evento.target.value }))} className="w-[150px]" /></td><td className="p-2 text-right"><Button size="sm" onClick={() => { if (!horaCorte) { toast.error("Informe a hora de corte."); return; } salvarMut.mutate({ tipoEnvioId: corte.tipoEnvioId, tipoTransporteId: corte.tipoTransporteId, horaCorte }); }} disabled={salvarMut.isLoading}>Salvar</Button></td></tr>;
          })}</tbody>
        </table>
        {(!cortes || (cortes as any[]).length === 0) && <p className="p-4 text-center text-sm text-muted-foreground">Nenhuma combinação de envio e transportadora cadastrada.</p>}
      </div>
    </div>
  );
}

function CrudVinculo({ user }: { user: any }) {
  const [selectedEmitenteId, setSelectedEmitenteId] = useState<number | null>(null);
  const [marketplaceId, setMarketplaceId] = useState<string>("__none__");
  const [tipoEnvioId, setTipoEnvioId] = useState<string>("__none__");
  const [tipoTransporteId, setTipoTransporteId] = useState<string>("__none__");
  const utils = trpc.useUtils();
  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const emitenteIdResolvido = (user as any)?.emitenteId ?? selectedEmitenteId;
  const { data: vinculos } = trpc.mecom.vinculos.list.useQuery(emitenteIdResolvido ? { emitenteId: emitenteIdResolvido } : undefined);
  const { data: marketplaces } = trpc.mecom.marketplaces.list.useQuery(emitenteIdResolvido ? { emitenteId: emitenteIdResolvido } : undefined);
  const { data: tiposEnvio } = trpc.mecom.tiposEnvio.list.useQuery();
  const { data: tiposTransporte } = trpc.mecom.tiposTransporte.list.useQuery();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTransporteId, setEditTransporteId] = useState<string>("__none__");
  const createMut = trpc.mecom.vinculos.create.useMutation({ onSuccess: () => { utils.mecom.vinculos.list.invalidate(); setMarketplaceId("__none__"); setTipoEnvioId("__none__"); setTipoTransporteId("__none__"); toast.success("Vínculo criado!"); } });
  const deleteMut = trpc.mecom.vinculos.delete.useMutation({ onSuccess: () => { utils.mecom.vinculos.list.invalidate(); toast.success("Removido!"); } });
  const updateMut = trpc.mecom.vinculos.update.useMutation({ onSuccess: () => { utils.mecom.vinculos.list.invalidate(); setEditingId(null); toast.success("Transporte atualizado!"); } });

  return (
    <div className="space-y-3 mt-3">
      {!(user as any)?.emitenteId && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Emitente:</label>
          <Select value={selectedEmitenteId?.toString() ?? "__none__"} onValueChange={(v) => setSelectedEmitenteId(v === "__none__" ? null : Number(v))}>
            <SelectTrigger className="w-[250px]"><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Selecione...</SelectItem>
              {(emitentes as any[] || []).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex gap-2 items-end flex-wrap">
        <div>
          <label className="text-sm font-medium">Loja</label>
          <Select value={marketplaceId} onValueChange={setMarketplaceId}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Selecione...</SelectItem>
              {(marketplaces as any[] || []).map((m: any) => (
                <SelectItem key={m.id} value={String(m.id)}>{m.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Tipo de Envio</label>
          <Select value={tipoEnvioId} onValueChange={setTipoEnvioId}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Selecione...</SelectItem>
              {(tiposEnvio as any[] || []).map((t: any) => (
                <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Tipo de Transporte</label>
          <Select value={tipoTransporteId} onValueChange={setTipoTransporteId}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Selecione...</SelectItem>
              {(tiposTransporte as any[] || []).map((t: any) => (
                <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => {
          if (marketplaceId === "__none__" || tipoEnvioId === "__none__" || tipoTransporteId === "__none__") { toast.error("Selecione todos os campos."); return; }
          if (!emitenteIdResolvido) { toast.error("Selecione o emitente."); return; }
          createMut.mutate({ emitenteId: emitenteIdResolvido, marketplaceId: Number(marketplaceId), tipoEnvioId: Number(tipoEnvioId), tipoTransporteId: Number(tipoTransporteId) });
        }} disabled={createMut.isLoading}><Plus className="w-4 h-4 mr-1" />Vincular</Button>
      </div>
      <div className="border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted"><tr><th className="p-2 text-left">Loja</th><th className="p-2 text-left">Envio</th><th className="p-2 text-left">Transporte</th><th className="p-2 text-right">Ações</th></tr></thead>
          <tbody>
            {(vinculos as any[] || []).map((v: any) => (
              <tr key={v.id} className="border-t">
                <td className="p-2">{v.marketplaceNome}</td>
                <td className="p-2">{v.envioNome}</td>
                <td className="p-2">{editingId === v.id ? (
                  <Select value={editTransporteId} onValueChange={setEditTransporteId}>
                    <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(tiposTransporte as any[] || []).map((t: any) => (
                        <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : v.transporteNome}</td>
                <td className="p-2 text-right flex gap-1 justify-end">
                  {editingId === v.id ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => { updateMut.mutate({ id: v.id, tipoTransporteId: Number(editTransporteId) }); }}>Salvar</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancelar</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(v.id); setEditTransporteId(String(v.tipoTransporteId)); }}>Editar</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => deleteMut.mutate({ id: v.id })}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
            {(!vinculos || (vinculos as any[]).length === 0) && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Nenhum vínculo cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CrudMarketplace({ user }: { user: any }) {
  const [nome, setNome] = useState("");
  const [selectedEmitenteId, setSelectedEmitenteId] = useState<number | null>(null);
  const utils = trpc.useUtils();
  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const emitenteIdResolvido = (user as any)?.emitenteId ?? selectedEmitenteId;
  const { data: items } = trpc.mecom.marketplaces.list.useQuery(emitenteIdResolvido ? { emitenteId: emitenteIdResolvido } : undefined);
  const createMut = trpc.mecom.marketplaces.create.useMutation({ onSuccess: () => { utils.mecom.marketplaces.list.invalidate(); setNome(""); toast.success("Criado!"); } });
  const deleteMut = trpc.mecom.marketplaces.delete.useMutation({ onSuccess: () => { utils.mecom.marketplaces.list.invalidate(); toast.success("Removido!"); } });

  const handleCreate = () => {
    if (!nome.trim()) return;
    if (!emitenteIdResolvido) { toast.error("Selecione o emitente."); return; }
    createMut.mutate({ nome: nome.trim(), emitenteId: emitenteIdResolvido });
  };

  return (
    <div className="space-y-3 mt-3">
      {!(user as any)?.emitenteId && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Emitente:</label>
          <Select value={selectedEmitenteId?.toString() ?? "__none__"} onValueChange={(v) => setSelectedEmitenteId(v === "__none__" ? null : Number(v))}>
            <SelectTrigger className="w-[250px]"><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Selecione...</SelectItem>
              {(emitentes as any[] || []).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex gap-2">
        <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do marketplace/loja" className="max-w-sm" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
        <Button onClick={handleCreate} disabled={createMut.isLoading}><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
      </div>
      <div className="border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted"><tr><th className="p-2 text-left">Nome</th><th className="p-2 text-right">Ações</th></tr></thead>
          <tbody>
            {(items as any[] || []).map((item: any) => (
              <tr key={item.id} className="border-t"><td className="p-2">{item.nome}</td><td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => deleteMut.mutate({ id: item.id })}><Trash2 className="w-4 h-4 text-red-500" /></Button></td></tr>
            ))}
            {(!items || (items as any[]).length === 0) && <tr><td colSpan={2} className="p-4 text-center text-muted-foreground">Nenhum cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SIGNATURE CANVAS (local, idêntico ao Expedicao.tsx)
// ═══════════════════════════════════════════════════════════════════
function MecomSignatureCanvas({ onSignature }: { onSignature: (base64: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDrawing.current = true;
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    isDrawing.current = false;
    const canvas = canvasRef.current!;
    onSignature(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onSignature("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="border rounded cursor-crosshair touch-none w-full"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <Button variant="ghost" size="sm" className="mt-1 text-xs" onClick={clear}>Limpar assinatura</Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RECEBER DEVOLUÇÕES TAB
// ═══════════════════════════════════════════════════════════════════
function ReceberDevolucoesTab() {
  const [marketplaceId, setMarketplaceId] = useState("__none__");
  const [tipoEnvioId, setTipoEnvioId] = useState("__none__");
  const [codigoBip, setCodigoBip] = useState("");
  const [codigosBip, setCodigosBip] = useState<string[]>([]);
  const [recebedorNome, setRecebedorNome] = useState("");
  const [recebedorRg, setRecebedorRg] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [assinaturaBase64, setAssinaturaBase64] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();
  const { data: marketplaces } = trpc.mecom.marketplaces.list.useQuery();
  const { data: tiposEnvio } = trpc.mecom.tiposEnvio.list.useQuery();
  const { data: tiposTransporte } = trpc.mecom.tiposTransporte.list.useQuery();
  const { data: vinculos } = trpc.mecom.vinculos.list.useQuery();
  const { data: recibos, isLoading: recibosCarregando } = trpc.mecom.listRecibosRecebimentoDevolucao.useQuery();

  const vinculoSelecionado = useMemo(() => (vinculos as any[] || []).find((v: any) =>
    v.marketplaceId === Number(marketplaceId) && v.tipoEnvioId === Number(tipoEnvioId)
  ), [vinculos, marketplaceId, tipoEnvioId]);
  const transportadoraVinculada = (tiposTransporte as any[] || []).find((tipo: any) => tipo.id === vinculoSelecionado?.tipoTransporteId)?.nome || "-";

  const receberMut = trpc.mecom.receberDevolucoes.useMutation({
    onSuccess: (resultado) => {
      toast.success(resultado.whatsappEnviado ? "Recebimento e recibo registrados." : "Recebimento e recibo registrados. O envio de WhatsApp não foi concluído.");
      setCodigoBip("");
      setCodigosBip([]);
      setRecebedorNome("");
      setRecebedorRg("");
      setWhatsapp("");
      setAssinaturaBase64("");
      utils.mecom.listRecibosRecebimentoDevolucao.invalidate();
      utils.mecom.pedidos.invalidate();
      forceFocus(inputRef);
    },
    onError: (erro) => toast.error(`Erro ao receber devoluções: ${erro.message}`),
  });

  const adicionarCodigo = () => {
    const codigo = normalizeCodBip(codigoBip);
    setCodigoBip("");
    if (!codigo) return;
    if (codigosBip.includes(codigo)) {
      toast.error("Este código já foi incluído no recibo.");
      forceFocus(inputRef);
      return;
    }
    setCodigosBip((atuais) => [...atuais, codigo]);
    forceFocus(inputRef);
  };

  const formatarWhatsApp = (valor: string) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    if (numeros.length <= 2) return numeros ? `(${numeros}` : "";
    if (numeros.length <= 6) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    if (numeros.length <= 10) return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
  };

  const gerarPdf = (dataHoraTexto: string) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margem = 15;
    let y = 17;
    const desenharCabecalho = () => {
      doc.setFillColor(234, 88, 12);
      doc.rect(0, 0, 210, 11, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text('BOX WAREHOUSE', margem, 7);
      doc.setTextColor(31, 41, 55);
      doc.setFontSize(16);
      doc.text('RECIBO DE RECEBIMENTO DE DEVOLUÇÕES', margem, 22);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Recebido em: ${dataHoraTexto}`, margem, 29);
      y = 37;
    };
    desenharCabecalho();
    doc.setFillColor(255, 247, 237);
    doc.roundedRect(margem, y, 180, 20, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Loja: ${(marketplaces as any[] || []).find((loja: any) => loja.id === Number(marketplaceId))?.nome || '-'}`, margem + 5, y + 7);
    doc.text(`Tipo de envio: ${(tiposEnvio as any[] || []).find((tipo: any) => tipo.id === Number(tipoEnvioId))?.nome || '-'}`, margem + 5, y + 13);
    doc.text(`Transportadora: ${transportadoraVinculada}`, margem + 95, y + 7);
    doc.setFontSize(13);
    doc.text(`${codigosBip.length} PACOTES`, margem + 95, y + 14);
    y += 29;
    doc.setFillColor(249, 250, 251);
    doc.rect(margem, y, 180, 8, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Nº', margem + 4, y + 5.5);
    doc.text('CÓDIGO BIP', margem + 22, y + 5.5);
    y += 8;
    doc.setFont('helvetica', 'normal');
    codigosBip.forEach((codigo, indice) => {
      if (y > 244) {
        doc.addPage();
        desenharCabecalho();
        doc.setFillColor(249, 250, 251);
        doc.rect(margem, y, 180, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('Nº', margem + 4, y + 5.5);
        doc.text('CÓDIGO BIP', margem + 22, y + 5.5);
        doc.setFont('helvetica', 'normal');
        y += 8;
      }
      doc.setDrawColor(229, 231, 235);
      doc.line(margem, y, 195, y);
      doc.text(String(indice + 1), margem + 4, y + 5.5);
      doc.setFont('courier', 'normal');
      doc.text(codigo, margem + 22, y + 5.5);
      doc.setFont('helvetica', 'normal');
      y += 8;
    });
    y += 9;
    if (y > 218) { doc.addPage(); desenharCabecalho(); }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DECLARAÇÃO DE RECEBIMENTO', margem, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const declaracao = 'Declaro que recebi os pacotes listados acima para as devoluções, nas condições registradas neste recibo.';
    const linhas = doc.splitTextToSize(declaracao, 180);
    doc.text(linhas, margem, y);
    y += linhas.length * 4.5 + 8;
    doc.setFont('helvetica', 'bold');
    doc.text(`Recebedor: ${recebedorNome}`, margem, y);
    doc.text(`RG: ${recebedorRg}`, margem + 105, y);
    y += 8;
    doc.text('Assinatura digital do recebedor', margem, y);
    y += 3;
    if (assinaturaBase64) doc.addImage(assinaturaBase64, 'PNG', margem, y, 75, 28);
    doc.setDrawColor(156, 163, 175);
    doc.line(margem, y + 29, margem + 75, y + 29);
    return doc.output('datauristring');
  };

  const confirmarRecebimento = async () => {
    if (marketplaceId === "__none__" || tipoEnvioId === "__none__") {
      toast.error("Selecione a loja e o tipo de envio.");
      return;
    }
    if (!vinculoSelecionado) {
      toast.error("Não há vínculo de transportadora para a Loja + Envio selecionados.");
      return;
    }
    if (!codigosBip.length) {
      toast.error("Bipe ao menos um pacote.");
      return;
    }
    if (!recebedorNome.trim() || !recebedorRg.trim()) {
      toast.error("Informe o nome e o RG do recebedor.");
      return;
    }
    if (!/^\(\d{2}\) \d{4,5}-\d{4}$/.test(whatsapp)) {
      toast.error("Informe o WhatsApp no formato (xx) xxxxx-xxxx ou (xx) xxxx-xxxx.");
      return;
    }
    if (!assinaturaBase64) {
      toast.error("A assinatura digital é obrigatória.");
      return;
    }
    const dataHoraTexto = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short'
    }).format(new Date());
    await receberMut.mutateAsync({
      marketplaceId: Number(marketplaceId),
      tipoEnvioId: Number(tipoEnvioId),
      codigosBip,
      recebedorNome: recebedorNome.trim(),
      recebedorRg: recebedorRg.trim(),
      whatsapp,
      assinaturaBase64,
      pdfBase64: gerarPdf(dataHoraTexto),
      dataHoraTexto,
    });
  };

  return (
    <div className="space-y-5 mt-4">
      <div>
        <h2 className="text-lg font-semibold">Receber Devoluções</h2>
        <p className="text-sm text-muted-foreground">Registre a entrada de devoluções, gere o recibo assinado e envie pelo WhatsApp informado.</p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm font-medium">Loja</label>
              <Select value={marketplaceId} onValueChange={setMarketplaceId}>
                <SelectTrigger><SelectValue placeholder="Selecione a loja" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Selecione...</SelectItem>
                  {(marketplaces as any[] || []).map((loja: any) => <SelectItem key={loja.id} value={String(loja.id)}>{loja.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Tipo de envio</label>
              <Select value={tipoEnvioId} onValueChange={setTipoEnvioId}>
                <SelectTrigger><SelectValue placeholder="Selecione o envio" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Selecione...</SelectItem>
                  {(tiposEnvio as any[] || []).filter((tipo: any) => tipo.nome === 'Devolução').map((tipo: any) => <SelectItem key={tipo.id} value={String(tipo.id)}>{tipo.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Transportadora</label>
              <Input value={transportadoraVinculada} readOnly className="bg-muted" />
            </div>
          </div>
          <div className="flex gap-2">
            <Input ref={inputRef} value={codigoBip} onChange={(evento) => setCodigoBip(evento.target.value)} onKeyDown={(evento) => evento.key === 'Enter' && adicionarCodigo()} placeholder="Bipe o código de devolução..." className="font-mono" />
            <Button type="button" onClick={adicionarCodigo}><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
          </div>
          {codigosBip.length > 0 && (
            <div className="rounded border p-3">
              <div className="mb-2 text-sm font-medium">Pacotes do recibo: {codigosBip.length}</div>
              <div className="flex flex-wrap gap-2">
                {codigosBip.map((codigo) => <Button key={codigo} type="button" size="sm" variant="outline" className="font-mono" onClick={() => setCodigosBip((atuais) => atuais.filter((item) => item !== codigo))}>{codigo} ×</Button>)}
              </div>
            </div>
          )}
          <div>
            <label className="text-sm font-medium">Assinatura digital do recebedor</label>
            {marketplaceId === "__none__" || tipoEnvioId === "__none__" || codigosBip.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">Selecione a loja, o envio e ao menos um pacote para gerar o QR.</p>
            ) : !assinaturaBase64 ? (
              <PublicSignatureQr
                key={`devolucao-${marketplaceId}-${tipoEnvioId}-${codigosBip.join('-')}`}
                tipo="DEVOLUCAO"
                titulo="Receber Devoluções"
                descricao={`${codigosBip.length} pacote(s) de devolução aguardando assinatura.`}
                referencia={{ marketplaceId: Number(marketplaceId), tipoEnvioId: Number(tipoEnvioId), codigosBip }}
                onSigned={(dados) => {
                  setRecebedorNome(dados.nomeAssinante);
                  setRecebedorRg(dados.documentoAssinante);
                  setWhatsapp(dados.whatsapp || "");
                  setAssinaturaBase64(dados.assinaturaBase64);
                }}
              />
            ) : <div className="mt-1 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">Assinatura recebida. Confira os dados e gere o recibo.</div>}
          </div>
          <div className="flex justify-end"><Button className="bg-green-600 hover:bg-green-700 text-white" onClick={confirmarRecebimento} disabled={receberMut.isPending}>{receberMut.isPending ? 'Registrando...' : 'Receber devoluções e gerar recibo'}</Button></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Recibos emitidos</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted"><tr><th className="p-2 text-left">Recebido em</th><th className="p-2 text-left">Loja</th><th className="p-2 text-left">Transportadora</th><th className="p-2 text-left">Recebedor</th><th className="p-2 text-right">Pacotes</th><th className="p-2 text-right">Arquivo</th></tr></thead>
            <tbody>
              {recibosCarregando && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Carregando recibos...</td></tr>}
              {!recibosCarregando && (recibos as any[] || []).map((recibo: any) => <tr key={recibo.id} className="border-t"><td className="p-2 whitespace-nowrap">{recibo.criadoEm ? new Date(recibo.criadoEm).toLocaleString('pt-BR') : '-'}</td><td className="p-2">{recibo.marketplaceNome || '-'}</td><td className="p-2">{recibo.transporteNome || '-'}</td><td className="p-2">{recibo.recebedorNome || '-'}</td><td className="p-2 text-right">{recibo.totalPacotes ?? 0}</td><td className="p-2 text-right"><Button asChild size="sm" variant="outline"><a href={recibo.documentoPdfPath} target="_blank" rel="noreferrer"><Download className="w-4 h-4 mr-1" />Abrir PDF</a></Button></td></tr>)}
              {!recibosCarregando && (recibos as any[] || []).length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nenhum recibo emitido.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ROMANEIOS TAB
// ═══════════════════════════════════════════════════════════════════
function RomaneiosTab() {
  const { data: romaneios, isLoading } = trpc.mecom.listRomaneios.useQuery();

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Romaneios Emitidos</h2>
        <p className="text-sm text-muted-foreground">Consulte os romaneios já gerados e abra seus respectivos PDFs.</p>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="p-2 text-left">Emitido em</th>
              <th className="p-2 text-left">Loja(s)</th>
              <th className="p-2 text-right">Pacotes</th>
              <th className="p-2 text-left">Motorista</th>
              <th className="p-2 text-left">Placa</th>
              <th className="p-2 text-right">Arquivo</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Carregando romaneios...</td></tr>
            )}
            {!isLoading && (romaneios as any[] || []).map((romaneio: any) => (
              <tr key={romaneio.id} className="border-t">
                <td className="p-2 whitespace-nowrap">{romaneio.criadoEm ? new Date(romaneio.criadoEm).toLocaleString("pt-BR") : "-"}</td>
                <td className="p-2 max-w-md truncate" title={romaneio.lojas || ""}>{romaneio.lojas || "-"}</td>
                <td className="p-2 text-right">{romaneio.totalPacotes ?? 0}</td>
                <td className="p-2">{romaneio.motoristaNome || "-"}</td>
                <td className="p-2">{romaneio.placaVeiculo || "-"}</td>
                <td className="p-2 text-right">
                  {romaneio.documentoPdfPath ? (
                    <Button asChild size="sm" variant="outline">
                      <a href={romaneio.documentoPdfPath} target="_blank" rel="noreferrer"><Download className="w-4 h-4 mr-1" />Abrir PDF</a>
                    </Button>
                  ) : "-"}
                </td>
              </tr>
            ))}
            {!isLoading && (romaneios as any[] || []).length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nenhum romaneio emitido.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FINALIZAR EXPEDIR TAB
// ═══════════════════════════════════════════════════════════════════
function FinalizarExpedirTab({ user }: { user: any }) {
  const isAdmin = user?.role === "admin";

  // Filtros
  const [emitenteId, setEmitenteId] = useState<string>("__all__");
  const [tipoTransporteId, setTipoTransporteId] = useState<string>("__all__");
  const [tipoEnvioId, setTipoEnvioId] = useState<string>("__all__");
  const [marketplaceId, setMarketplaceId] = useState<string>("__all__");
  const [search, setSearch] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [periodoRapido, setPeriodoRapido] = useState<string>("hoje");
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");

  const getDateRange = () => {
    const hoje = new Date();
    const formatDate = (d: Date) => d.toISOString().split('T')[0];
    if (periodoRapido === 'hoje') return { dataInicio: formatDate(hoje), dataFim: formatDate(hoje) };
    if (periodoRapido === 'ontem') { const ontem = new Date(hoje.getTime() - 86400000); return { dataInicio: formatDate(ontem), dataFim: formatDate(ontem) }; }
    if (periodoRapido === '7dias') { const seteDias = new Date(hoje.getTime() - 7 * 86400000); return { dataInicio: formatDate(seteDias), dataFim: formatDate(hoje) }; }
    if (periodoRapido === 'custom' && dataInicio) return { dataInicio, dataFim: dataFim || undefined };
    return {};
  };
  const dateRange = getDateRange();

  // Seleção
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [motoristaNome, setMotoristaNome] = useState("");
  const [motoristaRg, setMotoristaRg] = useState("");
  const [placaVeiculo, setPlacaVeiculo] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [assinaturaBase64, setAssinaturaBase64] = useState("");

  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const emitenteIdNum = emitenteId !== "__all__" ? Number(emitenteId) : undefined;
  const { data: tiposTransporte } = trpc.mecom.tiposTransporte.list.useQuery();
  const { data: tiposEnvio } = trpc.mecom.tiposEnvio.list.useQuery();
  const { data: marketplaces } = trpc.mecom.marketplaces.list.useQuery(emitenteIdNum ? { emitenteId: emitenteIdNum } : undefined);
  const { data: vinculos } = trpc.mecom.vinculos.list.useQuery();

  // Filtros dinâmicos por interseção via mecom_vinculos
  const vinculosFiltrados = useMemo(() => {
    if (!vinculos) return [];
    return (vinculos as any[]).filter((v: any) => {
      if (emitenteId !== "__all__" && v.emitenteId !== Number(emitenteId)) return false;
      if (tipoTransporteId !== "__all__" && v.tipoTransporteId !== Number(tipoTransporteId)) return false;
      if (tipoEnvioId !== "__all__" && v.tipoEnvioId !== Number(tipoEnvioId)) return false;
      if (marketplaceId !== "__all__" && v.marketplaceId !== Number(marketplaceId)) return false;
      return true;
    });
  }, [vinculos, emitenteId, tipoTransporteId, tipoEnvioId, marketplaceId]);

  const transporteIdsDisponiveis = useMemo(() => new Set(vinculosFiltrados.map((v: any) => v.tipoTransporteId)), [vinculosFiltrados]);
  const envioIdsDisponiveis = useMemo(() => new Set(vinculosFiltrados.map((v: any) => v.tipoEnvioId)), [vinculosFiltrados]);
  const marketplaceIdsDisponiveis = useMemo(() => new Set(vinculosFiltrados.map((v: any) => v.marketplaceId)), [vinculosFiltrados]);

  useEffect(() => {
    if (tipoTransporteId !== "__all__" && vinculos && !transporteIdsDisponiveis.has(Number(tipoTransporteId))) setTipoTransporteId("__all__");
  }, [transporteIdsDisponiveis]);
  useEffect(() => {
    if (tipoEnvioId !== "__all__" && vinculos && !envioIdsDisponiveis.has(Number(tipoEnvioId))) setTipoEnvioId("__all__");
  }, [envioIdsDisponiveis]);
  useEffect(() => {
    if (marketplaceId !== "__all__" && vinculos && !marketplaceIdsDisponiveis.has(Number(marketplaceId))) setMarketplaceId("__all__");
  }, [marketplaceIdsDisponiveis]);

  const filters = {
    emitenteId: emitenteIdNum,
    tipoTransporteId: tipoTransporteId !== "__all__" ? Number(tipoTransporteId) : undefined,
    tipoEnvioId: tipoEnvioId !== "__all__" ? Number(tipoEnvioId) : undefined,
    marketplaceId: marketplaceId !== "__all__" ? Number(marketplaceId) : undefined,
    dataInicio: dateRange.dataInicio || undefined,
    dataFim: dateRange.dataFim || undefined,
    search: search || undefined,
  };

  const { data: pedidos, refetch } = trpc.mecom.listExpedidos.useQuery(filters);

  const utils = trpc.useUtils();
  const gerarRomaneioMut = trpc.mecom.gerarRomaneio.useMutation({
    onSuccess: () => {
      toast.success("Romaneio gerado com sucesso!");
      setShowModal(false);
      setSelectedIds([]);
      setModalStep(1);
      setMotoristaNome(""); setMotoristaRg(""); setPlacaVeiculo(""); setWhatsapp(""); setAssinaturaBase64("");
      utils.mecom.listExpedidos.invalidate();
    },
    onError: (err) => toast.error("Erro ao gerar romaneio: " + err.message),
  });

  const pedidosFiltrados = useMemo(() => {
    if (!pedidos) return [];
    return pedidos as any[];
  }, [pedidos]);

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const allIds = pedidosFiltrados.map((p: any) => p.id);
    if (selectedIds.length === allIds.length && allIds.every(id => selectedIds.includes(id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const allSelected = pedidosFiltrados.length > 0 && pedidosFiltrados.every((p: any) => selectedIds.includes(p.id));

  const pedidosSelecionados = useMemo(() => {
    return pedidosFiltrados.filter((p: any) => selectedIds.includes(p.id));
  }, [pedidosFiltrados, selectedIds]);

  const emitentesNaSelecao = useMemo(() => {
    const map = new Map<number, string>();
    pedidosSelecionados.forEach((p: any) => {
      if (p.emitenteId && !map.has(p.emitenteId)) {
        const em = (emitentes as any[] || []).find((e: any) => e.id === p.emitenteId);
        map.set(p.emitenteId, em?.razaoSocial || `Emitente ${p.emitenteId}`);
      }
    });
    return map;
  }, [pedidosSelecionados, emitentes]);

  const gerarPdfParaEmitente = async (emitenteIdLocal: number, emitenteName: string, pedidosDoEmitente: any[], motoristaNomeLocal: string, motoristaRgLocal: string, placaVeiculoLocal: string, assinaturaLocal: string, whatsappLocal: string, dataColeta: string) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const lm = 15;
    let y = 20;

    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('ROMANEIO DE COLETA', 105, y, { align: 'center' }); y += 8;

    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Emitente: ${emitenteName}`, lm, y); y += 6;
    doc.text(`Data da Coleta: ${dataColeta}`, lm, y); y += 6;
    doc.text(`Total de Pacotes: ${pedidosDoEmitente.length}`, lm, y); y += 10;

    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    const cols = ['Código', 'Marketplace', 'Envio', 'Transportadora'];
    const colW = [55, 45, 45, 45];
    let x = lm;
    cols.forEach((c, i) => { doc.text(c, x, y); x += colW[i]; });
    y += 5;
    doc.setFont('helvetica', 'normal');
    pedidosDoEmitente.forEach((p: any) => {
      if (y > 240) { doc.addPage(); y = 20; }
      x = lm;
      const vals = [
        String(p.codigoBip || '-').slice(0, 28),
        String(p.marketplaceNome || '-').slice(0, 22),
        String(p.envioNome || '-').slice(0, 22),
        String(p.transporteNome || '-').slice(0, 22),
      ];
      vals.forEach((v, i) => { doc.text(v, x, y); x += colW[i]; });
      y += 5;
    });

    y += 10;
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text('Dados do Motorista', lm, y); y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(`Nome: ${motoristaNomeLocal}`, lm, y); y += 6;
    doc.text(`RG: ${motoristaRgLocal}`, lm, y); y += 6;
    doc.text(`Placa: ${placaVeiculoLocal}`, lm, y); y += 6;
    if (whatsappLocal) { doc.text(`WhatsApp: ${whatsappLocal}`, lm, y); y += 6; }

    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Assinatura Digital', lm, y); y += 4;
    if (assinaturaLocal) {
      doc.addImage(assinaturaLocal, 'PNG', lm, y, 80, 30);
      y += 34;
    }

    return doc.output('datauristring');
  };

  const handleGerarRomaneio = async () => {
    if (!motoristaNome.trim() || !motoristaRg.trim() || !placaVeiculo.trim()) {
      toast.error("Preencha nome, RG e placa do motorista");
      return;
    }
    if (!assinaturaBase64) {
      toast.error("Assinatura digital obrigatória");
      return;
    }

    const dataColeta = new Date().toLocaleDateString('pt-BR') + ' ' + new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    for (const [emitenteIdLocal, emitenteName] of emitentesNaSelecao.entries()) {
      const pedidosDoEmitente = pedidosSelecionados.filter((p: any) => p.emitenteId === emitenteIdLocal);
      const pedidoIdsDoEmitente = pedidosDoEmitente.map((p: any) => p.id);

      const pdfBase64 = await gerarPdfParaEmitente(
        emitenteIdLocal, emitenteName, pedidosDoEmitente,
        motoristaNome, motoristaRg, placaVeiculo, assinaturaBase64, whatsapp, dataColeta
      );

      await gerarRomaneioMut.mutateAsync({
        emitenteId: emitenteIdLocal,
        pedidoIds: pedidoIdsDoEmitente,
        motoristaNome,
        motoristaRg,
        placaVeiculo,
        assinaturaBase64,
        whatsapp: whatsapp || undefined,
        pdfBase64,
      });
    }
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Filtros de período */}
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => setPeriodoRapido('hoje')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'hoje' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Hoje</button>
        <button onClick={() => setPeriodoRapido('ontem')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'ontem' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Ontem</button>
        <button onClick={() => setPeriodoRapido('7dias')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === '7dias' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Últimos 7 dias</button>
        <button onClick={() => setPeriodoRapido('todos')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'todos' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Todos</button>
        <button onClick={() => setPeriodoRapido('custom')} className={`px-3 py-1.5 rounded text-sm font-medium ${periodoRapido === 'custom' ? 'bg-orange-500 text-white' : 'bg-muted'}`}>Período</button>
        {periodoRapido === 'custom' && (
          <>
            <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            <span className="text-sm">até</span>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          </>
        )}
      </div>
      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        {isAdmin && (
          <Select value={emitenteId} onValueChange={setEmitenteId}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Emitente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {(emitentes as any[] || []).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={tipoTransporteId} onValueChange={setTipoTransporteId}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Transportadora" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas</SelectItem>
            {(tiposTransporte as any[] || []).filter((t: any) => !vinculos || tipoEnvioId === "__all__" && marketplaceId === "__all__" ? true : transporteIdsDisponiveis.has(t.id)).map((t: any) => (
              <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tipoEnvioId} onValueChange={setTipoEnvioId}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Envio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            {(tiposEnvio as any[] || []).filter((t: any) => !vinculos || tipoTransporteId === "__all__" && marketplaceId === "__all__" ? true : envioIdsDisponiveis.has(t.id)).map((t: any) => (
              <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={marketplaceId} onValueChange={setMarketplaceId}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Marketplace" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            {(marketplaces as any[] || []).filter((m: any) => !vinculos || tipoTransporteId === "__all__" && tipoEnvioId === "__all__" ? true : marketplaceIdsDisponiveis.has(m.id)).map((m: any) => (
              <SelectItem key={m.id} value={String(m.id)}>{m.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
            placeholder="Buscar por código"
            className="w-[200px]"
          />
          <Button size="sm" variant="outline" onClick={() => setSearch(searchInput)}>Buscar</Button>
          {search && <Button size="sm" variant="ghost" onClick={() => { setSearch(""); setSearchInput(""); }}>Limpar</Button>}
        </div>
      </div>

      {/* Barra de ações */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button size="sm" variant="outline" onClick={toggleSelectAll}>
          {allSelected ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
          {allSelected ? "Desmarcar Todos" : "Selecionar Todos"}
        </Button>
        {selectedIds.length > 0 && (
          <span className="text-sm text-muted-foreground">{selectedIds.length} selecionado(s)</span>
        )}
        {selectedIds.length > 0 && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { setModalStep(1); setShowModal(true); }}>
            <ClipboardCheck className="w-4 h-4 mr-1" />Gerar Romaneio
          </Button>
        )}
      </div>

      {/* Tabela */}
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="p-2 w-8"></th>
              <th className="p-2 text-left">Código</th>
              <th className="p-2 text-left">Marketplace</th>
              <th className="p-2 text-left">Envio</th>
              <th className="p-2 text-left">Transportadora</th>
              <th className="p-2 text-left">Data Expedição</th>
            </tr>
          </thead>
          <tbody>
            {(pedidosFiltrados as any[]).map((p: any) => (
              <tr key={p.id} className={`border-t cursor-pointer hover:bg-muted/50 ${selectedIds.includes(p.id) ? 'bg-orange-50' : ''}`} onClick={() => toggleSelect(p.id)}>
                <td className="p-2">
                  {selectedIds.includes(p.id) ? <CheckSquare className="w-4 h-4 text-orange-500" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                </td>
                <td className="p-2 font-mono">{p.codigoBip}</td>
                <td className="p-2">{p.marketplaceNome || '-'}</td>
                <td className="p-2">{p.envioNome || '-'}</td>
                <td className="p-2">{p.transporteNome || '-'}</td>
                <td className="p-2">{p.bipExpedicaoEm ? new Date(p.bipExpedicaoEm).toLocaleString('pt-BR') : '-'}</td>
              </tr>
            ))}
            {pedidosFiltrados.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nenhum pedido expedido sem romaneio.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Gerar Romaneio */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-2">Gerar Romaneio</h2>
            <div className="flex gap-1 mb-4">
              {[1,2,3,4].map(s => (
                <div key={s} className={`h-1.5 flex-1 rounded ${modalStep >= s ? 'bg-orange-500' : 'bg-muted'}`} />
              ))}
            </div>

            {/* Etapa 1: Resumo */}
            {modalStep === 1 && (
              <div className="space-y-3">
                <p className="text-sm">
                  Você está coletando <strong>{pedidosSelecionados.length}</strong> pacote(s) das lojas:{' '}
                  <strong>{Array.from(emitentesNaSelecao.values()).join(', ')}</strong>.
                </p>
                <p className="text-xs text-muted-foreground">Serão gerados {emitentesNaSelecao.size} arquivo(s) PDF — um por emitente.</p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                  <Button onClick={() => setModalStep(4)}>Gerar QR</Button>
                </div>
              </div>
            )}

            {/* Etapa 2: WhatsApp */}
            {modalStep === 2 && (
              <div className="space-y-3">
                <label className="text-sm font-medium">WhatsApp (opcional)</label>
                <p className="text-xs text-muted-foreground">Informe um número para receber cópia do romaneio assinado.</p>
                <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Ex: 11999999999" />
                <div className="flex justify-between gap-2 mt-4">
                  <Button variant="outline" onClick={() => setModalStep(1)}>Voltar</Button>
                  <Button onClick={() => setModalStep(3)}>Próximo</Button>
                </div>
              </div>
            )}

            {/* Etapa 3: Dados do motorista */}
            {modalStep === 3 && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Nome Completo</label>
                  <Input value={motoristaNome} onChange={(e) => setMotoristaNome(e.target.value)} placeholder="Nome completo do motorista" />
                </div>
                <div>
                  <label className="text-sm font-medium">RG</label>
                  <Input value={motoristaRg} onChange={(e) => setMotoristaRg(e.target.value)} placeholder="RG do motorista" />
                </div>
                <div>
                  <label className="text-sm font-medium">Placa do Veículo</label>
                  <Input value={placaVeiculo} onChange={(e) => setPlacaVeiculo(e.target.value)} placeholder="ABC-1234" />
                </div>
                <div className="flex justify-between gap-2 mt-4">
                  <Button variant="outline" onClick={() => setModalStep(2)}>Voltar</Button>
                  <Button onClick={() => setModalStep(4)}>Próximo</Button>
                </div>
              </div>
            )}

            {/* Etapa 4: Assinatura */}
            {modalStep === 4 && (
              <div className="space-y-3">
                <label className="text-sm font-medium">Assinatura Digital</label>
                <p className="text-xs text-muted-foreground">Use o QR para preencher os dados e assinar pelo celular.</p>
                {!assinaturaBase64 && <PublicSignatureQr
                  key={`romaneio-${selectedIds.join('-')}`}
                  tipo="ROMANEIO"
                  titulo="Gerar Romaneio"
                  descricao={`${pedidosSelecionados.length} pacote(s) selecionado(s) para coleta.`}
                  referencia={{ pedidoIds: selectedIds, emitenteIds: Array.from(emitentesNaSelecao.keys()) }}
                  onSigned={(dados) => {
                    setMotoristaNome(dados.nomeAssinante);
                    setMotoristaRg(dados.documentoAssinante);
                    setPlacaVeiculo(dados.placaVeiculo || "");
                    setWhatsapp(dados.whatsapp || "");
                    setAssinaturaBase64(dados.assinaturaBase64);
                  }}
                />}
                {assinaturaBase64 && <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">Assinatura recebida. Confira os dados e confirme o romaneio.</div>}
                <div className="flex justify-between gap-2 mt-4">
                  <Button variant="outline" onClick={() => setModalStep(1)}>Voltar</Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleGerarRomaneio}
                    disabled={gerarRomaneioMut.isPending || !assinaturaBase64}
                  >
                    {gerarRomaneioMut.isPending ? "Gerando..." : "Confirmar e Gerar"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
